import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-hot-toast";
import RazorpayButton from "../../Service/RazorpayButton";
import Swal from "sweetalert2";
import {
    CheckCircle, CreditCard, Loader2, Tag, ShoppingCart, Trash2, Edit3,
    Globe, X, Sparkles, Check, ChevronDown, Minus, Plus, User, ChevronUp,
    Crown, Star, Zap, Calendar, CalendarDays,
    Clock,
    AlertTriangle
} from "lucide-react";


const TicketSelectionStep = React.memo(function TicketSelectionStep({
    availableRegions, selectedRegion, setSelectedRegion, availableCategories, addToCart,
    cart, totalTickets, couponCode, couponCodeVerify, isChecking, handleCouponCodeChange,
    checkCouponCode, couponInputStyles, couponButtonStyles, removeFromCart, updateCartQuantity,
    cartSubtotal, finalAmount, proceedToParticipantDetails, isCartExpanded, setIsCartExpanded, couponData,
    // Auto promo props
    autoPromoApplied, autoPromoCategories, autoPromoDiscount, eventData,
    // Date selection props
    cartItemDates, setCartItemDates
}) {


    const navigate = useNavigate();
    const getCartItemQuantity = (region, category) => {
        const cartItemId = `${region}-${category}`;
        const item = cart.find(item => item.id === cartItemId);
        return item ? item.quantity : 0;
    };


    // Add state for managing dropdown visibility for each category
    const [expandedCategories, setExpandedCategories] = useState({});

    // Function to toggle category details dropdown
    const toggleCategoryDetails = (categoryIndex) => {
        setExpandedCategories(prev => ({
            ...prev,
            [categoryIndex]: !prev[categoryIndex]
        }));
    };

    // Check if dates are selected for a category
    const getCategoryDates = (region, categoryName) => {
        const cartItemId = `${region}-${categoryName}`;
        return cartItemDates[cartItemId] || [];
    };

    // Handle date selection toggle
    const handleDateToggle = (region, categoryName, date) => {
        const cartItemId = `${region}-${categoryName}`;
        const currentDates = getCategoryDates(region, categoryName);
        const dateStr = date.toISOString();
        const isSelected = currentDates.some(d => new Date(d).toISOString() === dateStr);

        // Find the category to get min/max limits
        const category = availableCategories.find(c => c.name === categoryName);
        const maxSelection = category?.maxDateSelection || 1;

        if (isSelected) {
            // Remove date
            const updatedDates = currentDates.filter(d => new Date(d).toISOString() !== dateStr);
            setCartItemDates(prev => ({
                ...prev,
                [cartItemId]: updatedDates
            }));
        } else {
            // Add date if under max limit
            if (currentDates.length < maxSelection) {
                const updatedDates = [...currentDates, date];
                setCartItemDates(prev => ({
                    ...prev,
                    [cartItemId]: updatedDates
                }));
            }
        }
    };

    // Check if a date is selected
    const isDateSelected = (region, categoryName, date) => {
        const categoryDates = getCategoryDates(region, categoryName);
        return categoryDates.some(d => new Date(d).toISOString() === date.toISOString());
    };

    // Validate date requirements before proceeding
    const validateDateRequirements = () => {
        const errors = [];

        cart.forEach(item => {
            const category = availableCategories.find(c => c.name === item.category);
            if (category?.enableDateSelection && category?.availableDates?.length > 0) {
                const selectedDates = getCategoryDates(item.region, item.category);
                const minRequired = category.minDateSelection || 1;

                if (selectedDates.length < minRequired) {
                    errors.push(`${item.category}: Select at least ${minRequired} date${minRequired > 1 ? 's' : ''}`);
                }
            }
        });

        return errors;
    };

    // Enhanced proceed function with validation
    const handleProceedToParticipantDetails = () => {
        if (cart.length === 0) {
            toast.error("Please add tickets to cart first");
            return;
        }

        // Validate date requirements
        const dateErrors = validateDateRequirements();
        if (dateErrors.length > 0) {
            toast.error(`Please complete date selection:\n${dateErrors.join('\n')}`);
            return;
        }

        proceedToParticipantDetails();
    };

    // Regular add to cart (no date selection needed)
    const handleAddToCart = (region, categoryData, quantity = 1) => {
        addToCart(region, categoryData.name, quantity);
    };

    // Update cart quantity and preserve dates
    const handleUpdateCartQuantity = (cartItemId, newQuantity) => {
        if (newQuantity <= 0) {
            // Remove dates when removing item completely
            setCartItemDates(prev => {
                const updated = { ...prev };
                delete updated[cartItemId];
                return updated;
            });
        }
        updateCartQuantity(cartItemId, newQuantity);
    };

    // Remove from cart and clear dates
    const handleRemoveFromCart = (cartItemId) => {
        setCartItemDates(prev => {
            const updated = { ...prev };
            delete updated[cartItemId];
            return updated;
        });
        removeFromCart(cartItemId);
    };




    return (
        <div className="space-y-4 md:space-y-6">
            {/* Auto Promo Banner */}
            {eventData?.autoPromoSettings?.enabled && (
                <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-xl p-4">
                    <div className="flex items-center space-x-2 mb-2">
                        <Zap className="w-5 h-5 text-purple-600" />
                        <h4 className="font-semibold text-purple-800">Bulk Discount Available!</h4>
                    </div>
                    <p className="text-sm text-purple-700">
                        Get {eventData.autoPromoSettings.discountPercentage}% off when you select {eventData.autoPromoSettings.minQuantity}+ tickets from the same category.
                    </p>
                    {autoPromoApplied && (
                        <div className="mt-2 bg-purple-100 border border-purple-300 rounded-lg p-2 flex items-center">
                            <Check className="w-4 h-4 text-purple-600 mr-2" />
                            <span className="text-sm font-medium text-purple-700">
                                Auto discount applied! Saving {selectedRegion === "indian" ? "₹" : "$"}{autoPromoDiscount}
                            </span>
                        </div>
                    )}
                </div>
            )}


            {/* Accommodation Notice Banner */}
            <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-4">
                <div className="flex items-center space-x-2">
                    <AlertTriangle className="w-5 h-5 text-amber-600" />
                    <p className="text-sm md:text-base font-medium text-amber-800">
                        Accommodation is not included in any of the ticket categories below.
                    </p>
                </div>
            </div>



            {/* Region Selection */}
            <div className="bg-white rounded-xl p-4 md:p-6 border border-slate-200 shadow-sm">
                <div className="flex justify-between">
                    <div className="flex items-center space-x-2 mb-4">
                        <div className="w-6 h-6 md:w-8 md:h-8 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-lg flex items-center justify-center">
                            <Globe className="w-3 h-3 md:w-4 md:h-4 text-white" />
                        </div>
                        <h3 className="text-base md:text-lg font-semibold text-gray-800">Select Your Region</h3>
                    </div>

                    <div>
                        <button onClick={() => navigate("/my-id-cards")} className="text-sm md:text-base text-indigo-600 hover:underline">
                            View All Id Cards
                        </button>
                    </div>
                </div>

                <div className="grid grid-cols-1 gap-3 md:grid-cols-2 md:gap-4">
                    {availableRegions.map((region) => (
                        <button
                            key={region}
                            onClick={() => {
                                setSelectedRegion(region);
                            }}
                            className={`p-3 md:p-4 border-2 rounded-xl transition-all duration-200 flex items-center space-x-3 ${selectedRegion === region
                                ? "border-blue-500 bg-blue-50 shadow-md"
                                : "border-gray-200 bg-white hover:border-blue-300 hover:shadow-sm"
                                }`}
                        >
                            {region.toLowerCase() === "indian" ? (
                                <span className="w-6 h-6 md:w-8 md:h-8 rounded-full overflow-hidden flex items-center justify-center bg-white">
                                    <IndianFlagIcon />
                                </span>
                            ) : (
                                <span className="text-xl md:text-2xl">🌍</span>
                            )}
                            <div className="text-left">
                                <h4 className={`text-sm md:text-base font-semibold ${selectedRegion === region ? "text-blue-700" : "text-gray-800"}`}>
                                    {region.charAt(0).toUpperCase() + region.slice(1)}
                                </h4>
                                <p className="text-xs md:text-sm text-gray-600">
                                    {region.toLowerCase() === "indian"
                                        ? "For Indian participants"
                                        : "For international participants"}
                                </p>
                            </div>
                        </button>
                    ))}
                </div>
            </div>

            {/* Mobile Cart - Shows only when there are items */}
            {selectedRegion && cart.length > 0 && (
                <div className="lg:hidden bg-white rounded-xl border border-slate-200 shadow-sm">
                    <button
                        onClick={() => setIsCartExpanded(!isCartExpanded)}
                        className="w-full p-4 flex items-center justify-between text-left"
                    >
                        <div className="flex items-center space-x-3">
                            <ShoppingCart className="w-5 h-5 text-blue-600" />
                            <div>
                                <h3 className="font-semibold text-gray-800">
                                    Your Cart ({totalTickets} {totalTickets === 1 ? "ticket" : "tickets"})
                                </h3>
                                <p className="text-sm text-gray-600">
                                    Total: {selectedRegion === "indian" ? "₹" : "$"}{finalAmount}
                                    {autoPromoApplied && (
                                        <span className="text-green-600 ml-2">
                                            ({eventData?.autoPromoSettings?.discountPercentage}% off applied!)
                                        </span>
                                    )}
                                </p>
                            </div>
                        </div>
                        <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${isCartExpanded ? 'rotate-180' : ''}`} />
                    </button>

                    {isCartExpanded && (
                        <div className="border-t border-gray-200 p-4 space-y-4">
                            {/* Cart Items */}
                            {cart.map((item) => {
                                const isHighlightedItem = item.categoryData?.highlightTicket;
                                const itemDates = cartItemDates[item.id];
                                const category = availableCategories.find(c => c.name === item.category);
                                const hasDateRequirement = category?.enableDateSelection && category?.availableDates?.length > 0;
                                const minRequired = category?.minDateSelection || 1;
                                const hasValidDates = !hasDateRequirement || (itemDates && itemDates.length >= minRequired);

                                return (
                                    <div
                                        key={item.id}
                                        className={`border rounded-lg p-3 ${isHighlightedItem
                                            ? "border-amber-300 bg-gradient-to-br from-amber-50 to-orange-50"
                                            : "border-gray-200"
                                            }`}
                                    >
                                        <div className="flex justify-between items-start mb-2">
                                            <div className="flex-1">
                                                <div className="flex items-center space-x-2">
                                                    <h4 className={`font-medium text-sm ${isHighlightedItem ? "text-amber-900" : "text-gray-800"
                                                        }`}>
                                                        {item.category}
                                                    </h4>
                                                    {isHighlightedItem && (
                                                        <Crown className="w-3 h-3 text-amber-600" />
                                                    )}
                                                    {hasDateRequirement && (
                                                        <span className={`text-xs px-1 py-0.5 rounded ${hasValidDates
                                                            ? 'bg-green-100 text-green-700'
                                                            : 'bg-red-100 text-red-700'
                                                            }`}>
                                                            {hasValidDates ? '✓' : '!'}
                                                        </span>
                                                    )}
                                                </div>
                                                <p className="text-xs text-gray-600">
                                                    {item.region.charAt(0).toUpperCase() + item.region.slice(1)} Region
                                                </p>
                                                <p className={`text-sm font-semibold mt-1 ${isHighlightedItem ? "text-amber-800" : "text-gray-800"
                                                    }`}>
                                                    {item.region === "indian" ? "₹" : "$"}{item.price} each
                                                </p>

                                                {/* Show selected dates */}
                                                {itemDates && itemDates.length > 0 && (
                                                    <div className="mt-2 text-xs">
                                                        <span className="font-medium text-gray-600">Dates:</span>
                                                        <div className="flex flex-wrap gap-1 mt-1">
                                                            {itemDates.map((date, idx) => (
                                                                <span key={idx} className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded text-xs flex items-center space-x-1">
                                                                    <CalendarDays className="w-3 h-3" />
                                                                    <span>
                                                                        {new Date(date).toLocaleDateString('en-GB', {
                                                                            day: '2-digit',
                                                                            month: 'short'
                                                                        })}
                                                                    </span>
                                                                </span>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Date requirement warning */}
                                                {hasDateRequirement && !hasValidDates && (
                                                    <div className="mt-2 text-xs text-red-600 bg-red-50 px-2 py-1 rounded">
                                                        ⚠ Select at least {minRequired} date{minRequired > 1 ? 's' : ''}
                                                    </div>
                                                )}
                                            </div>
                                            <button
                                                onClick={() => handleRemoveFromCart(item.id)}
                                                className="text-red-500 hover:text-red-700 p-1"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </div>

                                        {/* Quantity Controls */}
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center space-x-2">
                                                <button
                                                    onClick={() => handleUpdateCartQuantity(item.id, item.quantity - 1)}
                                                    className="w-6 h-6 rounded-full border border-gray-300 flex items-center justify-center text-gray-600 hover:bg-gray-50"
                                                >
                                                    <Minus className="w-3 h-3" />
                                                </button>
                                                <span className="font-medium text-gray-800 w-6 text-center text-sm">
                                                    {item.quantity}
                                                </span>
                                                <button
                                                    onClick={() => handleAddToCart(item.region, item.categoryData, 1)}
                                                    className="w-6 h-6 rounded-full border border-gray-300 flex items-center justify-center text-gray-600 hover:bg-gray-50"
                                                >
                                                    <Plus className="w-3 h-3" />
                                                </button>
                                            </div>
                                            <span className={`font-semibold text-sm ${isHighlightedItem ? "text-amber-800" : "text-gray-800"
                                                }`}>
                                                {item.region === "indian" ? "₹" : "$"}{item.totalPrice}
                                            </span>
                                        </div>
                                    </div>
                                );
                            })}


                            {/* Coupon Code Section */}
                            <div className="border-t pt-4 mt-4">
                                <h4 className="text-sm font-medium text-gray-700 mb-2">Promotional Code</h4>
                                <div className="flex gap-2">
                                    <input
                                        id="couponCodeInputMobile"
                                        type="text"
                                        value={couponCode}
                                        onChange={handleCouponCodeChange}
                                        className={`flex-1 h-9 px-3 border rounded-lg focus:ring-2 focus:outline-none uppercase font-mono text-xs transition-all duration-200 ${couponInputStyles}`}
                                        placeholder="ENTER CODE"
                                        disabled={isChecking}
                                    />
                                    <button
                                        className={`px-3 py-2 rounded-lg text-white font-medium transition-all duration-200 flex items-center justify-center min-w-[60px] text-xs ${couponButtonStyles}`}
                                        onClick={checkCouponCode}
                                        disabled={isChecking || !couponCode.trim()}
                                    >
                                        {isChecking ? (
                                            <Loader2 className="w-3 h-3 animate-spin" />
                                        ) : couponCodeVerify === true ? (
                                            <Check className="w-3 h-3" />
                                        ) : couponCodeVerify === false ? (
                                            <X className="w-3 h-3" />
                                        ) : (
                                            "APPLY"
                                        )}
                                    </button>
                                </div>

                                {couponCodeVerify === true && (
                                    <div className="mt-2 bg-green-50 border border-green-200 rounded-lg p-2 flex items-center text-green-700">
                                        <Check className="w-3 h-3 mr-2" />
                                        <span className="text-xs font-medium">Coupon applied successfully!</span>
                                    </div>
                                )}

                                {couponCodeVerify === false && (
                                    <div className="mt-2 bg-red-50 border border-red-200 rounded-lg p-2 flex items-center text-red-700">
                                        <X className="w-3 h-3 mr-2" />
                                        <span className="text-xs font-medium">Invalid coupon code.</span>
                                    </div>
                                )}
                            </div>

                            {/* Total Section */}
                            <div className="border-t pt-4 mt-4 space-y-2">
                                <div className="flex justify-between text-sm">
                                    <span>Subtotal:</span>
                                    <span className={((couponCodeVerify && finalAmount !== cartSubtotal) || (autoPromoApplied && autoPromoDiscount > 0)) ? "line-through text-gray-500" : "font-semibold"}>
                                        {selectedRegion === "indian" ? "₹" : "$"}{cartSubtotal}
                                    </span>
                                </div>



                                {couponCodeVerify && finalAmount !== cartSubtotal && (
                                    <>
                                        <div className="flex justify-between text-sm text-green-600">
                                            <span>Coupon Discount:</span>
                                            <span>-{selectedRegion === "indian" ? "₹" : "$"}{cartSubtotal - finalAmount}</span>
                                        </div>
                                        <div className="flex justify-between font-semibold text-base border-t pt-2">
                                            <span>Total:</span>
                                            <span>{selectedRegion === "indian" ? "₹" : "$"}{finalAmount}</span>
                                        </div>
                                    </>
                                )}
                                {!couponCodeVerify && autoPromoApplied && autoPromoDiscount > 0 && (
                                    <>
                                        <div className="flex justify-between text-sm text-purple-600">
                                            <span>Auto Bulk Discount:</span>
                                            <span>-{selectedRegion === "indian" ? "₹" : "$"}{autoPromoDiscount}</span>
                                        </div>
                                        <div className="flex justify-between font-semibold text-base border-t pt-2">
                                            <span>Total:</span>
                                            <span>{selectedRegion === "indian" ? "₹" : "$"}{finalAmount}</span>
                                        </div>
                                    </>
                                )}
                                {!couponCodeVerify && !autoPromoApplied && (
                                    <div className="flex justify-between font-semibold text-base border-t pt-2">
                                        <span>Total:</span>
                                        <span>{selectedRegion === "indian" ? "₹" : "$"}{finalAmount}</span>
                                    </div>
                                )}
                            </div>

                            {/* Mobile Proceed Button */}
                            <button
                                onClick={handleProceedToParticipantDetails}
                                className="w-full bg-gradient-to-r from-blue-500 to-purple-600 text-white font-medium py-3 px-4 rounded-lg hover:from-blue-600 hover:to-purple-700 transition-all duration-200 flex items-center justify-center space-x-2"
                            >
                                <span>Proceed to Details</span>
                                <ChevronDown className="w-4 h-4 rotate-[-90deg]" />
                            </button>
                        </div>
                    )}
                </div>
            )}

            {/* Ticket Selection */}
            {selectedRegion && (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
                    {/* Mobile: Full width tickets, Desktop: 2/3 width */}
                    <div className="lg:col-span-2 space-y-4 md:space-y-6">
                        <div className="bg-white rounded-xl p-4 md:p-6 border border-slate-200 shadow-sm">
                            <div className="flex items-center space-x-2 mb-4">
                                <div className="w-6 h-6 md:w-8 md:h-8 bg-gradient-to-r from-purple-500 to-pink-600 rounded-lg flex items-center justify-center">
                                    <Tag className="w-3 h-3 md:w-4 md:h-4 text-white" />
                                </div>
                                <h3 className="text-base md:text-lg font-semibold text-gray-800">
                                    {selectedRegion.charAt(0).toUpperCase() + selectedRegion.slice(1)} Tickets
                                </h3>
                            </div>

                            <div className="grid gap-3 md:gap-4">
                                {availableCategories.map((category, categoryIndex) => {
                                    const currencySymbol = selectedRegion === "indian" ? "₹" : "$";
                                    const hasEarlyBird = category.earlyBirdPrice && category.regularPrice;
                                    const effectivePrice = category.earlyBirdPrice || category.regularPrice || category.price || 0;
                                    const savings = hasEarlyBird ? category.regularPrice - category.earlyBirdPrice : 0;
                                    const isHighlighted = category.highlightTicket;

                                    // Check if this category qualifies for auto promo
                                    const cartItemId = `${selectedRegion}-${category.name}`;
                                    const currentQuantity = getCartItemQuantity(selectedRegion, category.name);
                                    const hasAutoPromo = autoPromoCategories.includes(cartItemId);

                                    // Get selected dates for this category
                                    const categoryDates = getCategoryDates(selectedRegion, category.name);
                                    const hasDatesSelected = categoryDates.length > 0;
                                    const hasDateRequirement = category.enableDateSelection && category.availableDates?.length > 0;
                                    const minRequired = category.minDateSelection || 1;
                                    const maxAllowed = category.maxDateSelection || 1;
                                    const hasValidDates = !hasDateRequirement || categoryDates.length >= minRequired;

                                    return (
                                        <div
                                            key={`${category.name}-${categoryIndex}`}
                                            className={`relative border rounded-lg p-3 md:p-4 transition-all duration-200 ${isHighlighted
                                                ? "border-2 border-orange-300  bg-gradient-to-br from-yellow-50 via-amber-50 to-orange-50 shadow-lg hover:shadow-xl"
                                                : "border-2 border-blue-300 shadow-md hover:shadow-lg "
                                                }`}
                                        >
                                            {/* Most Valuable Badge - Top Left */}
                                            {isHighlighted && (
                                                <div className="absolute -top-2 -left-2 z-10">
                                                    <div className="bg-gradient-to-r from-yellow-400 via-amber-500 to-orange-500 text-white px-2 py-1 md:px-4 md:py-3 rounded-lg shadow-lg flex items-center space-x-1 text-xs md:text-base font-bold animate-slow-pulse">
                                                        <Crown size={16} className="md:w-6 md:h-6" />
                                                        <span className="hidden md:inline">MOST VALUABLE</span>
                                                        <span className="md:hidden">TOP</span>
                                                        <Star size={16} className="md:w-6 md:h-6" />
                                                    </div>
                                                </div>
                                            )}

                                            {/* Auto Promo Badge */}
                                            {hasAutoPromo && (
                                                <div className={`flex justify-end mb-2 ${isHighlighted ? 'mt-4' : ''}`}>
                                                    <span className="bg-purple-100 text-purple-800 text-xs font-medium px-2 py-1 rounded-full flex items-center space-x-1">
                                                        <Zap className="w-3 h-3" />
                                                        <span>Auto {eventData?.autoPromoSettings?.discountPercentage}% Off</span>
                                                    </span>
                                                </div>
                                            )}

                                            {/* Early Bird Badge */}
                                            {hasEarlyBird && !hasAutoPromo ? (
                                                <div className={`flex justify-end mb-2 ${isHighlighted ? 'mt-4' : ''}`}>
                                                    <span className="bg-green-100 text-green-800 text-xs font-medium px-2 py-1 rounded-full">
                                                        Early Bird
                                                    </span>
                                                </div>
                                            ) : null}

                                            <div className="flex justify-between items-start gap-3">
                                                <div className="flex-1 min-w-0">
                                                    <h5 className={`font-semibold mb-2 text-sm md:text-base ${isHighlighted ? "text-amber-900" : "text-gray-800"
                                                        }`}>
                                                        {category.name}
                                                    </h5>

                                                    {/* Show bulk discount info */}
                                                    {eventData?.autoPromoSettings?.enabled && currentQuantity > 0 && currentQuantity < eventData.autoPromoSettings.minQuantity && !couponCodeVerify && (
                                                        <div className="mb-2 text-xs text-purple-600 bg-purple-50 px-2 py-1 rounded">
                                                            Add {eventData.autoPromoSettings.minQuantity - currentQuantity} more for {eventData.autoPromoSettings.discountPercentage}% off
                                                        </div>
                                                    )}

                                                    {/* Pricing */}
                                                    <div className="flex items-center space-x-2 mb-2 flex-wrap">
                                                        <span className={`text-lg md:text-xl font-bold ${isHighlighted ? "text-amber-800" : "text-gray-800"
                                                            }`}>
                                                            {currencySymbol}{effectivePrice}
                                                        </span>
                                                        {hasEarlyBird ? (
                                                            <span className="text-xs md:text-sm text-gray-500 line-through">
                                                                {currencySymbol}{category.regularPrice}
                                                            </span>
                                                        ) : null}
                                                        {savings > 0 ? (
                                                            <span className={`text-xs md:text-sm font-medium ${isHighlighted ? "text-orange-600" : "text-green-600"
                                                                }`}>
                                                                Save {currencySymbol}{savings}
                                                            </span>
                                                        ) : null}
                                                    </div>

                                                    {/* Daily Rate */}
                                                    {category.effectiveDailyRate > 0 ? (
                                                        <div className={`text-xs px-2 py-1 rounded-full inline-block mb-2 ${isHighlighted
                                                            ? "bg-amber-100 text-amber-800"
                                                            : "bg-blue-100 text-blue-800"
                                                            }`}>
                                                            Daily: {currencySymbol}{category.effectiveDailyRate}
                                                        </div>
                                                    ) : null}
                                                </div>

                                                {/* Add to Cart Controls */}
                                                <div className="flex flex-col items-end space-y-2 flex-shrink-0">
                                                    {(() => {
                                                        if (currentQuantity === 0) {
                                                            return (
                                                                <button
                                                                    onClick={() => handleAddToCart(selectedRegion, category, 1)}
                                                                    className={`px-3 py-2 md:px-4 md:py-2 rounded-lg text-xs md:text-sm font-medium transition-colors duration-200 ${isHighlighted
                                                                        ? "bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white shadow-lg"
                                                                        : "bg-blue-500 hover:bg-blue-600 text-white"
                                                                        }`}
                                                                >
                                                                    Add to Cart
                                                                </button>
                                                            );
                                                        } else {
                                                            return (
                                                                <div className="flex flex-col items-center space-y-1">
                                                                    <span className={`text-xs font-medium ${isHighlighted ? "text-amber-700" : "text-gray-600"
                                                                        }`}>
                                                                        In Cart
                                                                    </span>
                                                                    <div className={`flex items-center space-x-1 md:space-x-2 rounded-lg p-1 md:p-2 border ${isHighlighted
                                                                        ? "bg-amber-50 border-amber-200"
                                                                        : "bg-blue-50 border-blue-200"
                                                                        }`}>
                                                                        <button
                                                                            onClick={() => {
                                                                                const cartItemId = `${selectedRegion}-${category.name}`;
                                                                                handleUpdateCartQuantity(cartItemId, currentQuantity - 1);
                                                                            }}
                                                                            className={`w-5 h-5 md:w-6 md:h-6 rounded-full text-white flex items-center justify-center text-xs ${isHighlighted
                                                                                ? "bg-amber-500 hover:bg-amber-600"
                                                                                : "bg-blue-500 hover:bg-blue-600"
                                                                                }`}
                                                                        >
                                                                            <Minus className="w-2 h-2 md:w-3 md:h-3" />
                                                                        </button>
                                                                        <span className={`font-semibold min-w-[16px] md:min-w-[20px] text-center text-xs md:text-sm ${isHighlighted ? "text-amber-700" : "text-blue-700"
                                                                            }`}>
                                                                            {currentQuantity}
                                                                        </span>
                                                                        <button
                                                                            onClick={() => handleAddToCart(selectedRegion, category, 1)}
                                                                            className={`w-5 h-5 md:w-6 md:h-6 rounded-full text-white flex items-center justify-center text-xs ${isHighlighted
                                                                                ? "bg-amber-500 hover:bg-amber-600"
                                                                                : "bg-blue-500 hover:bg-blue-600"
                                                                                }`}
                                                                        >
                                                                            <Plus className="w-2 h-2 md:w-3 md:h-3" />
                                                                        </button>
                                                                    </div>

                                                                    {/* Date validation indicator for items in cart */}
                                                                    {hasDateRequirement && (
                                                                        <div className={`text-xs px-2 py-1 rounded-full ${hasValidDates
                                                                            ? 'bg-green-100 text-green-700'
                                                                            : 'bg-red-100 text-red-700 animate-pulse'
                                                                            }`}>
                                                                            {hasValidDates ? '✓ Dates OK' : '⚠ Select Dates'}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            );
                                                        }
                                                    })()}
                                                </div>
                                            </div>

                                            <div >
                                                {/* Inline Date Selection - Above descriptions - Mobile First */}
                                                {hasDateRequirement && (
                                                    <div className="mb-3 p-2 md:p-3 bg-blue-50 border border-blue-200 rounded-lg">
                                                        <div className="flex items-center justify-between mb-2">
                                                            <div className="flex items-center space-x-1 md:space-x-2">
                                                                <Calendar className="w-3 h-3 md:w-4 md:h-4 text-blue-600" />
                                                                <span className="text-xs md:text-sm font-medium text-blue-800">
                                                                    Select Dates ({categoryDates.length}/{maxAllowed})
                                                                </span>
                                                            </div>
                                                            <span className={`text-xs px-1 md:px-2 py-0.5 md:py-1 rounded-full text-xs ${hasValidDates
                                                                ? 'bg-green-100 text-green-700'
                                                                : 'bg-red-100 text-red-700'
                                                                }`}>
                                                                {hasValidDates ? 'Valid' : `Need ${minRequired - categoryDates.length}`}
                                                            </span>
                                                        </div>

                                                        <p className="text-xs text-blue-700 mb-2">
                                                            Select {minRequired} to {maxAllowed} dates for this ticket
                                                        </p>

                                                        <div className="flex flex-wrap gap-2 md:gap-2">
                                                            {category.availableDates.map((date, dateIdx) => {
                                                                const dateObj = new Date(date);
                                                                const isSelected = isDateSelected(selectedRegion, category.name, dateObj);
                                                                const canSelect = !isSelected && categoryDates.length < maxAllowed;

                                                                return (
                                                                    <button
                                                                        key={dateIdx}
                                                                        onClick={() => handleDateToggle(selectedRegion, category.name, dateObj)}
                                                                        disabled={!isSelected && !canSelect}
                                                                        className={`p-1.5 md:p-2 rounded-md md:rounded-lg text-xs font-medium transition-all duration-200 border ${isSelected
                                                                            ? 'bg-blue-500 text-white border-blue-600 shadow-sm'
                                                                            : canSelect
                                                                                ? 'bg-white text-blue-700 border-blue-300 hover:bg-blue-100 active:bg-blue-200'
                                                                                : 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                                                                            }`}
                                                                    >
                                                                        <div className="flex items-center gap-1">
                                                                            <span className="font-semibold text-xs md:text-sm">
                                                                                {dateObj.toLocaleDateString('en-GB', { day: '2-digit' })}
                                                                            </span>
                                                                            <span className="text-xs opacity-90">
                                                                                {dateObj.toLocaleDateString('en-GB', { month: 'short' })}
                                                                            </span>
                                                                        </div>
                                                                    </button>
                                                                );
                                                            })}
                                                        </div>

                                                        {/* Show selected dates summary */}
                                                        {hasDatesSelected && (
                                                            <div className="mt-2 md:mt-3 pt-2 md:pt-3 border-t border-blue-200">
                                                                <div className="text-xs text-blue-700 mb-1 md:mb-2">Selected Dates:</div>
                                                                <div className="flex flex-wrap gap-1">
                                                                    {categoryDates.map((date, idx) => (
                                                                        <span key={idx} className="bg-blue-500 text-white px-1.5 md:px-2 py-0.5 md:py-1 rounded text-xs">
                                                                            {new Date(date).toLocaleDateString('en-GB', {
                                                                                day: '2-digit',
                                                                                month: 'short'
                                                                            })}
                                                                        </span>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}

                                                {/* Category Details with Simple Dropdown */}
                                                {category.headerDescriptions?.length > 0 && (
                                                    <div className="mt-3 pt-3 border-t border-gray-200">
                                                        <button
                                                            onClick={() => toggleCategoryDetails(categoryIndex)}
                                                            className={`flex items-center justify-between w-full text-left text-xs font-medium transition-colors duration-200 ${isHighlighted
                                                                ? 'text-amber-700 hover:text-amber-800'
                                                                : 'text-gray-700 hover:text-gray-800'
                                                                }`}
                                                        >
                                                            <span className="flex items-center gap-1">
                                                                {expandedCategories[categoryIndex] ? 'Read Less' : 'Read More'} <ChevronDown
                                                                    className={`w-4 h-4 transition-transform duration-300 font-semibold ${expandedCategories[categoryIndex] ? 'rotate-180' : 'rotate-0'
                                                                        } ${isHighlighted ? 'text-amber-600' : 'text-gray-500'}`}
                                                                />
                                                            </span>

                                                        </button>

                                                        <div className={`overflow-hidden transition-all duration-300 ease-in-out ${expandedCategories[categoryIndex]
                                                            ? 'max-h-96 opacity-100 mt-2'
                                                            : 'max-h-0 opacity-0'
                                                            }`}>
                                                            <div className="space-y-2">
                                                                {category.headerDescriptions.map((hd, hdIndex) => (
                                                                    <div key={hdIndex} className="text-xs text-gray-600">
                                                                        <h6 className={`font-medium ${isHighlighted ? "text-amber-700" : "text-gray-700"
                                                                            }`}>
                                                                            {hd.header}
                                                                        </h6>
                                                                        <p className="whitespace-pre-wrap mt-1">
                                                                            {hd.description}
                                                                        </p>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}

                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>

                    {/* Desktop: Shopping Cart Sidebar - Same as before but with validation indicators */}
                    <div className="hidden lg:block space-y-6">
                        <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm sticky top-6">
                            <div className="flex items-center space-x-2 mb-4">
                                <ShoppingCart className="w-5 h-5 text-blue-600" />
                                <h3 className="text-lg font-semibold text-gray-800">
                                    Your Cart ({totalTickets} {totalTickets === 1 ? "ticket" : "tickets"})
                                </h3>
                            </div>

                            {cart.length === 0 ? (
                                <div className="text-center py-8">
                                    <ShoppingCart className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                                    <p className="text-gray-500">Your cart is empty</p>
                                    <p className="text-sm text-gray-400 mt-1">Add tickets to get started</p>
                                </div>
                            ) : (
                                <div className="space-y-4">
                                    {/* Cart Items */}
                                    {cart.map((item) => {
                                        const isHighlightedItem = item.categoryData?.highlightTicket;
                                        const itemDates = cartItemDates[item.id];
                                        const category = availableCategories.find(c => c.name === item.category);
                                        const hasDateRequirement = category?.enableDateSelection && category?.availableDates?.length > 0;
                                        const minRequired = category?.minDateSelection || 1;
                                        const hasValidDates = !hasDateRequirement || (itemDates && itemDates.length >= minRequired);

                                        return (
                                            <div
                                                key={item.id}
                                                className={`border rounded-lg p-3 ${isHighlightedItem
                                                    ? "border-amber-300 bg-gradient-to-br from-amber-50 to-orange-50"
                                                    : "border-gray-200"
                                                    }`}
                                            >
                                                <div className="flex justify-between items-start mb-2">
                                                    <div className="flex-1">
                                                        <div className="flex items-center space-x-2">
                                                            <h4 className={`font-medium text-sm ${isHighlightedItem ? "text-amber-900" : "text-gray-800"
                                                                }`}>
                                                                {item.category}
                                                            </h4>
                                                            {isHighlightedItem && (
                                                                <Crown className="w-3 h-3 text-amber-600" />
                                                            )}
                                                            {hasDateRequirement && (
                                                                <span className={`text-xs px-1 py-0.5 rounded ${hasValidDates
                                                                    ? 'bg-green-100 text-green-700'
                                                                    : 'bg-red-100 text-red-700'
                                                                    }`}>
                                                                    {hasValidDates ? '✓' : '!'}
                                                                </span>
                                                            )}
                                                        </div>
                                                        <p className="text-xs text-gray-600">
                                                            {item.region.charAt(0).toUpperCase() + item.region.slice(1)} Region
                                                        </p>
                                                        <p className={`text-sm font-semibold mt-1 ${isHighlightedItem ? "text-amber-800" : "text-gray-800"
                                                            }`}>
                                                            {item.region === "indian" ? "₹" : "$"}{item.price} each
                                                        </p>

                                                        {/* Show selected dates */}
                                                        {itemDates && itemDates.length > 0 && (
                                                            <div className="mt-2 text-xs">
                                                                <span className="font-medium text-gray-600">Dates:</span>
                                                                <div className="flex flex-wrap gap-1 mt-1">
                                                                    {itemDates.map((date, idx) => (
                                                                        <span key={idx} className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded text-xs flex items-center space-x-1">
                                                                            <CalendarDays className="w-3 h-3" />
                                                                            <span>
                                                                                {new Date(date).toLocaleDateString('en-GB', {
                                                                                    day: '2-digit',
                                                                                    month: 'short'
                                                                                })}
                                                                            </span>
                                                                        </span>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        )}

                                                        {/* Date requirement warning */}
                                                        {hasDateRequirement && !hasValidDates && (
                                                            <div className="mt-2 text-xs text-red-600 bg-red-50 px-2 py-1 rounded">
                                                                ⚠ Select at least {minRequired} date{minRequired > 1 ? 's' : ''}
                                                            </div>
                                                        )}
                                                    </div>
                                                    <button
                                                        onClick={() => handleRemoveFromCart(item.id)}
                                                        className="text-red-500 hover:text-red-700 p-1"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                </div>

                                                {/* Quantity Controls */}
                                                <div className="flex items-center justify-between">
                                                    <div className="flex items-center space-x-2">
                                                        <button
                                                            onClick={() => handleUpdateCartQuantity(item.id, item.quantity - 1)}
                                                            className="w-7 h-7 rounded-full border border-gray-300 flex items-center justify-center text-gray-600 hover:bg-gray-50"
                                                        >
                                                            <Minus className="w-3 h-3" />
                                                        </button>
                                                        <span className="font-medium text-gray-800 w-8 text-center">
                                                            {item.quantity}
                                                        </span>
                                                        <button
                                                            onClick={() => handleAddToCart(item.region, item.categoryData, 1)}
                                                            className="w-7 h-7 rounded-full border border-gray-300 flex items-center justify-center text-gray-600 hover:bg-gray-50"
                                                        >
                                                            <Plus className="w-3 h-3" />
                                                        </button>
                                                    </div>
                                                    <span className={`font-semibold ${isHighlightedItem ? "text-amber-800" : "text-gray-800"
                                                        }`}>
                                                        {item.region === "indian" ? "₹" : "$"}{item.totalPrice}
                                                    </span>
                                                </div>
                                            </div>
                                        );
                                    })}

                                    {/* Coupon Code Section - Same as before */}
                                    <div className="border-t pt-4 mt-4">
                                        <h4 className="text-sm font-medium text-gray-700 mb-2">Promotional Code</h4>
                                        <div className="flex gap-2">
                                            <input
                                                id="couponCodeInput"
                                                type="text"
                                                value={couponCode}
                                                onChange={handleCouponCodeChange}
                                                className={`flex-1 h-9 px-3 border rounded-lg focus:ring-2 focus:outline-none uppercase font-mono text-xs transition-all duration-200 ${couponInputStyles}`}
                                                placeholder="ENTER CODE"
                                                disabled={isChecking}
                                            />
                                            <button
                                                className={`px-3 py-2 rounded-lg text-white font-medium transition-all duration-200 flex items-center justify-center min-w-[60px] text-xs ${couponButtonStyles}`}
                                                onClick={checkCouponCode}
                                                disabled={isChecking || !couponCode.trim()}
                                            >
                                                {isChecking ? (
                                                    <Loader2 className="w-3 h-3 animate-spin" />
                                                ) : couponCodeVerify === true ? (
                                                    <Check className="w-3 h-3" />
                                                ) : couponCodeVerify === false ? (
                                                    <X className="w-3 h-3" />
                                                ) : (
                                                    "APPLY"
                                                )}
                                            </button>
                                        </div>

                                        {couponCodeVerify === true && (
                                            <div className="mt-2 bg-green-50 border border-green-200 rounded-lg p-2 flex items-center text-green-700">
                                                <Check className="w-3 h-3 mr-2" />
                                                <span className="text-xs font-medium">Coupon applied successfully!</span>
                                            </div>
                                        )}

                                        {couponCodeVerify === false && (
                                            <div className="mt-2 bg-red-50 border border-red-200 rounded-lg p-2 flex items-center text-red-700">
                                                <X className="w-3 h-3 mr-2" />
                                                <span className="text-xs font-medium">Invalid coupon code.</span>
                                            </div>
                                        )}
                                    </div>

                                    {/* Total Section - Same as before */}
                                    <div className="border-t pt-4 mt-4 space-y-2">
                                        <div className="flex justify-between text-sm">
                                            <span>Subtotal:</span>
                                            <span className={((couponCodeVerify && finalAmount !== cartSubtotal) || (autoPromoApplied && autoPromoDiscount > 0)) ? "line-through text-gray-500" : "font-semibold"}>
                                                {selectedRegion === "indian" ? "₹" : "$"}{cartSubtotal}
                                            </span>
                                        </div>


                                        {couponCodeVerify && finalAmount !== cartSubtotal && (
                                            <>
                                                <div className="flex justify-between text-sm text-green-600">
                                                    <span>Coupon Discount:</span>
                                                    <span>-{selectedRegion === "indian" ? "₹" : "$"}{cartSubtotal - finalAmount}</span>
                                                </div>
                                                <div className="flex justify-between font-semibold text-lg border-t pt-2">
                                                    <span>Total:</span>
                                                    <span>{selectedRegion === "indian" ? "₹" : "$"}{finalAmount}</span>
                                                </div>
                                            </>
                                        )}
                                        {!couponCodeVerify && autoPromoApplied && autoPromoDiscount > 0 && (
                                            <>
                                                <div className="flex justify-between text-sm text-purple-600">
                                                    <span>Auto Bulk Discount:</span>
                                                    <span>-{selectedRegion === "indian" ? "₹" : "$"}{autoPromoDiscount}</span>
                                                </div>
                                                <div className="flex justify-between font-semibold text-lg border-t pt-2">
                                                    <span>Total:</span>
                                                    <span>{selectedRegion === "indian" ? "₹" : "$"}{finalAmount}</span>
                                                </div>
                                            </>
                                        )}
                                        {!couponCodeVerify && !autoPromoApplied && (
                                            <div className="flex justify-between font-semibold text-lg border-t pt-2">
                                                <span>Total:</span>
                                                <span>{selectedRegion === "indian" ? "₹" : "$"}{finalAmount}</span>
                                            </div>
                                        )}
                                    </div>

                                    {/* Proceed Button with validation */}
                                    <button
                                        onClick={handleProceedToParticipantDetails}
                                        className="w-full bg-gradient-to-r from-blue-500 to-purple-600 text-white font-medium py-3 px-4 rounded-lg hover:from-blue-600 hover:to-purple-700 transition-all duration-200 flex items-center justify-center space-x-2"
                                    >
                                        <span>Proceed to Details</span>
                                        <ChevronDown className="w-4 h-4 rotate-[-90deg]" />
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            <style>{`
                @keyframes slow-pulse {
                    0%, 100% {
                        opacity: 1;
                    }
                    50% {
                        opacity: 0.79;
                    }
                }
                
                .animate-slow-pulse {
                    animation: slow-pulse 2s ease-in-out infinite;
                }
            `}</style>
        </div>
    );
});





const ParticipantDetailsStep = React.memo(function ParticipantDetailsStep({
    participantDetails,
    updateParticipantDetail,
    cart,
    selectedRegion,
    finalAmount,
    setCurrentStep,
    cartItemDates, cartSubtotal, totalTickets, couponCodeVerify, couponCode, couponData,
    autoPromoApplied, autoPromoDiscount, autoPromoCategories, eventData
}) {
    const [countries, setCountries] = useState([]);

    // Fetch country codes from REST Countries
    useEffect(() => {
        axios
            .get("https://restcountries.com/v3.1/all?fields=name,cca2,idd")
            .then((res) => {
                const formatted = res.data
                    .map((c) => {
                        const dial =
                            c.idd && c.idd.root
                                ? c.idd.suffixes && c.idd.suffixes.length
                                    ? c.idd.suffixes.map((s) => c.idd.root + s)
                                    : [c.idd.root]
                                : [];
                        return {
                            name: c.name.common,
                            code: c.cca2,
                            dial_code: dial[0] || null,
                        };
                    })
                    .filter((c) => c.dial_code); // remove nulls
                setCountries(formatted.sort((a, b) => a.name.localeCompare(b.name)));
            })
            .catch((err) => console.error(err));
    }, []);

    // Function to get default country code based on region
    const getDefaultCountryCode = (region) => {
        return region === "indian" ? "+91" : "+1"; // Default to +1 (US) for international
    };

    // Function to extract country name from dial code
    const getCountryFromDialCode = (dialCode) => {
        const country = countries.find(c => c.dial_code === dialCode);
        return country ? country.name : '';
    };

    // Function to handle country code change
    const handleCountryCodeChange = (index, countryCode) => {
        // Update country code
        updateParticipantDetail(index, "countryCode", countryCode);

        // Auto-populate country field for international participants
        const detail = participantDetails[index];
        if (detail.region === "international") {
            const extractedCountry = getCountryFromDialCode(countryCode);
            if (extractedCountry) {
                updateParticipantDetail(index, "country", extractedCountry);
            }
        }
    };

    // Function to get full phone number (country code + phone)
    const getFullPhoneNumber = (countryCode, phone) => {
        if (!countryCode || !phone) return '';
        // Remove any non-digit characters from phone number
        const digitsOnly = phone.replace(/\D/g, '');
        return `${countryCode}${digitsOnly}`;
    };

    // Function to handle first name or last name change and update full name
    const handleNameChange = (index, field, value) => {
        // Update the specific field (firstName or lastName)
        updateParticipantDetail(index, field, value);

        // Get current detail
        const currentDetail = participantDetails[index];

        // Calculate the full name
        let firstName = field === "firstName" ? value : (currentDetail.firstName || '');
        let lastName = field === "lastName" ? value : (currentDetail.lastName || '');

        // Create full name by combining first and last name
        const fullName = `${firstName.trim()} ${lastName.trim()}`.trim();

        // Update the full name field as well
        updateParticipantDetail(index, "fullName", fullName);
    };

    return (
        <div className="space-y-4 md:space-y-6 pb-24 lg:pb-0">
            <div className="bg-white rounded-xl p-4 md:p-6 border border-slate-200 shadow-sm">
                <div className="flex items-center justify-between mb-4 md:mb-6">
                    <div className="flex items-center space-x-2">
                        <div className="w-6 h-6 md:w-8 md:h-8 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
                            <User className="w-3 h-3 md:w-4 md:h-4 text-white" />
                        </div>
                        <h3 className="text-base md:text-lg font-semibold text-gray-800">Participant Details</h3>
                    </div>
                    <button
                        onClick={() => setCurrentStep(1)}
                        className="text-blue-600 hover:text-blue-800 text-xs md:text-sm font-medium flex items-center space-x-1"
                    >
                        <span>← Back to Tickets</span>
                    </button>
                </div>

                <div className="grid gap-4 md:gap-6 max-h-96 overflow-y-auto custom-scrollbar">
                    {participantDetails.map((detail, index) => {
                        const itemDates = cartItemDates?.[detail.cartItemId];

                        // Get default country code if not set
                        const currentCountryCode = detail.countryCode || getDefaultCountryCode(detail.region);

                        // Check if country code should be disabled (Indian region)
                        const isCountryCodeDisabled = detail.region === "indian";

                        // Get full phone number for display purposes (for debugging)
                        const fullPhoneNumber = getFullPhoneNumber(currentCountryCode, detail.phone);

                        return (
                            <div key={detail._rowId ?? index} className="border border-gray-200 rounded-lg p-3 md:p-4">
                                <div className="flex items-center justify-between mb-3 md:mb-4">
                                    <h4 className="font-semibold text-gray-800 text-sm md:text-base">
                                        Participant {index + 1}
                                    </h4>
                                    <div className="flex flex-col items-end space-y-1">
                                        <div className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                                            {detail.category} - {detail.region}
                                        </div>

                                        {/* Show selected dates if any */}
                                        {itemDates && itemDates.length > 0 && (
                                            <div className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full flex items-center space-x-1">
                                                <CalendarDays className="w-3 h-3" />
                                                <span>{itemDates.length} date{itemDates.length > 1 ? 's' : ''}</span>
                                            </div>
                                        )}

                                        {/* Debug: Show full phone number */}
                                        {fullPhoneNumber && (
                                            <div className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full">
                                                Full: {fullPhoneNumber}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Show selected dates details */}
                                {itemDates && itemDates.length > 0 && (
                                    <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                                        <div className="flex items-center space-x-2 mb-2">
                                            <CalendarDays className="w-4 h-4 text-green-600" />
                                            <span className="text-sm font-medium text-green-800">Selected Dates:</span>
                                        </div>
                                        <div className="flex flex-wrap gap-2">
                                            {itemDates.map((date, idx) => (
                                                <span key={idx} className="bg-white text-green-800 px-2 py-1 rounded text-xs border border-green-300">
                                                    {new Date(date).toLocaleDateString('en-GB', {
                                                        weekday: 'short',
                                                        day: '2-digit',
                                                        month: 'short',
                                                        year: 'numeric'
                                                    })}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
                                    {/* First Name */}
                                    <div>
                                        <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1">
                                            First Name *
                                        </label>
                                        <input
                                            type="text"
                                            value={detail.firstName || ''}
                                            onChange={(e) => handleNameChange(index, "firstName", e.target.value)}
                                            className="w-full h-9 md:h-10 px-3 bg-gray-50 border border-gray-300 rounded-lg focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200 text-sm"
                                            placeholder="Enter first name"
                                        />
                                        {detail.errors?.firstName && (
                                            <p className="text-red-500 text-xs mt-1">{detail.errors.firstName}</p>
                                        )}
                                    </div>

                                    {/* Last Name */}
                                    <div>
                                        <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1">
                                            Last Name *
                                        </label>
                                        <input
                                            type="text"
                                            value={detail.lastName || ''}
                                            onChange={(e) => handleNameChange(index, "lastName", e.target.value)}
                                            className="w-full h-9 md:h-10 px-3 bg-gray-50 border border-gray-300 rounded-lg focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200 text-sm"
                                            placeholder="Enter last name"
                                        />
                                        {detail.errors?.lastName && (
                                            <p className="text-red-500 text-xs mt-1">{detail.errors.lastName}</p>
                                        )}
                                    </div>

                                    {/* Phone Number */}
                                    <div>
                                        <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1">
                                            Phone Number *
                                        </label>
                                        <div className="flex items-center gap-2">
                                            {/* Country code dropdown */}
                                            <select
                                                value={currentCountryCode}
                                                onChange={(e) => handleCountryCodeChange(index, e.target.value)}
                                                disabled={isCountryCodeDisabled}
                                                className={`w-[120px] h-9 md:h-10 px-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-200 text-sm ${isCountryCodeDisabled
                                                    ? 'bg-gray-100 text-gray-600 cursor-not-allowed'
                                                    : 'bg-gray-50 focus:border-green-500'
                                                    }`}
                                            >
                                                {detail.region === "indian" ? (
                                                    <option value="+91">India (+91)</option>
                                                ) : (
                                                    countries.map((c) => (
                                                        <option key={c.code} value={c.dial_code}>
                                                            {c.name} ({c.dial_code})
                                                        </option>
                                                    ))
                                                )}
                                            </select>
                                            <input
                                                type="text"
                                                value={detail.phone}
                                                onChange={(e) => updateParticipantDetail(index, "phone", e.target.value)}
                                                className="w-full h-9 md:h-10 px-3 bg-gray-50 border border-gray-300 rounded-lg focus:bg-white focus:border-green-500 focus:ring-2 focus:ring-green-200 transition-all duration-200 text-sm"
                                                placeholder="Enter phone number"
                                            />
                                        </div>
                                        {detail.errors?.phone && (
                                            <p className="text-red-500 text-xs mt-1">{detail.errors.phone}</p>
                                        )}
                                    </div>

                                    {/* Email */}
                                    <div>
                                        <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1">
                                            Email Address *
                                        </label>
                                        <input
                                            type="email"
                                            value={detail.email}
                                            onChange={(e) => updateParticipantDetail(index, "email", e.target.value)}
                                            className="w-full h-9 md:h-10 px-3 bg-gray-50 border border-gray-300 rounded-lg focus:bg-white focus:border-orange-500 focus:ring-2 focus:ring-orange-200 transition-all duration-200 text-sm"
                                            placeholder="Enter email address"
                                        />
                                        {detail.errors?.email && (
                                            <p className="text-red-500 text-xs mt-1">{detail.errors.email}</p>
                                        )}
                                    </div>

                                    {/* Conditional Fields based on Region */}
                                    {detail.region === "indian" ? (
                                        <>
                                            {/* City for Indian participants */}
                                            <div>
                                                <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1">
                                                    City *
                                                </label>
                                                <input
                                                    type="text"
                                                    value={detail.city || ''}
                                                    onChange={(e) => updateParticipantDetail(index, "city", e.target.value)}
                                                    className="w-full h-9 md:h-10 px-3 bg-gray-50 border border-gray-300 rounded-lg focus:bg-white focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-all duration-200 text-sm"
                                                    placeholder="Enter city"
                                                />
                                                {detail.errors?.city && (
                                                    <p className="text-red-500 text-xs mt-1">{detail.errors.city}</p>
                                                )}
                                            </div>

                                            {/* State for Indian participants */}
                                            <div>
                                                <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1">
                                                    State *
                                                </label>
                                                <input
                                                    type="text"
                                                    value={detail.state || ''}
                                                    onChange={(e) => updateParticipantDetail(index, "state", e.target.value)}
                                                    className="w-full h-9 md:h-10 px-3 bg-gray-50 border border-gray-300 rounded-lg focus:bg-white focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-all duration-200 text-sm"
                                                    placeholder="Enter state"
                                                />
                                                {detail.errors?.state && (
                                                    <p className="text-red-500 text-xs mt-1">{detail.errors.state}</p>
                                                )}
                                            </div>
                                        </>
                                    ) : (
                                        <>
                                            {/* Country for International participants */}
                                            <div>
                                                <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1">
                                                    Country *
                                                    <span className="text-xs text-gray-500 ml-1">(auto-filled from country code, can edit)</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    value={detail.country || ''}
                                                    onChange={(e) => updateParticipantDetail(index, "country", e.target.value)}
                                                    className="w-full h-9 md:h-10 px-3 bg-gray-50 border border-gray-300 rounded-lg focus:bg-white focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-all duration-200 text-sm"
                                                    placeholder="Country name (auto-filled from code selection)"
                                                />
                                                {detail.errors?.country && (
                                                    <p className="text-red-500 text-xs mt-1">{detail.errors.country}</p>
                                                )}
                                            </div>

                                            {/* City for International participants */}
                                            <div>
                                                <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1">
                                                    City *
                                                </label>
                                                <input
                                                    type="text"
                                                    value={detail.city || ''}
                                                    onChange={(e) => updateParticipantDetail(index, "city", e.target.value)}
                                                    className="w-full h-9 md:h-10 px-3 bg-gray-50 border border-gray-300 rounded-lg focus:bg-white focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-all duration-200 text-sm"
                                                    placeholder="Enter city"
                                                />
                                                {detail.errors?.city && (
                                                    <p className="text-red-500 text-xs mt-1">{detail.errors.city}</p>
                                                )}
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Enhanced Order Summary with Dates - remains the same */}
            {/* Enhanced Order Summary with Complete Money Breakdown */}
            <div className="bg-white rounded-xl p-4 md:p-6 border border-slate-200 shadow-sm">
                <h3 className="text-base md:text-lg font-semibold text-gray-800 mb-4 md:mb-6">Order Summary</h3>

                {/* Items List */}
                <div className="space-y-3 md:space-y-4 mb-4 md:mb-6">
                    {cart.map(item => {
                        const itemDates = cartItemDates[item.id] || [];
                        const isHighlighted = item.categoryData?.highlightTicket;

                        return (
                            <div key={item.id} className="flex justify-between items-start py-3 border-b border-gray-100 last:border-b-0">
                                <div className="flex-1">
                                    <div className="flex items-center space-x-2 mb-1">
                                        <h4 className={`font-medium text-sm md:text-base ${isHighlighted ? 'text-amber-800' : 'text-gray-800'
                                            }`}>
                                            {item.category}
                                        </h4>
                                        {isHighlighted && <Crown className="w-3 h-3 text-amber-600" />}
                                    </div>

                                    <div className="text-xs md:text-sm text-gray-600 space-y-1">
                                        <p>{item.region.charAt(0).toUpperCase() + item.region.slice(1)} Region</p>
                                        <p>
                                            <span className="font-medium">{item.quantity} × {selectedRegion === 'indian' ? '₹' : '$'}{item.price}</span>
                                            {item.quantity > 1 && (
                                                <span className="ml-2 text-gray-500">
                                                    ({item.quantity} participants)
                                                </span>
                                            )}
                                        </p>
                                    </div>

                                    {/* Show selected dates if any */}
                                    {itemDates && itemDates.length > 0 && (
                                        <div className="mt-2">
                                            <div className="text-xs text-gray-600 mb-1">Selected Dates:</div>
                                            <div className="flex flex-wrap gap-1">
                                                {itemDates.map((date, idx) => (
                                                    <span key={idx} className="bg-blue-100 text-blue-700 px-2 py-1 rounded-full text-xs flex items-center space-x-1">
                                                        <CalendarDays className="w-2.5 h-2.5" />
                                                        <span>{new Date(date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}</span>
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>

                                <div className="text-right ml-4">
                                    <span className={`font-semibold text-sm md:text-base ${isHighlighted ? 'text-amber-800' : 'text-gray-800'
                                        }`}>
                                        {selectedRegion === 'indian' ? '₹' : '$'}{item.totalPrice}
                                    </span>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Money Breakdown Section */}
                <div className="border-t border-gray-200 pt-4 space-y-3">
                    {/* Subtotal */}
                    <div className="flex justify-between items-center">
                        <span className="text-sm md:text-base text-gray-700">Subtotal ({totalTickets} tickets)</span>
                        <span className={`text-sm md:text-base font-medium ${(couponCodeVerify && finalAmount !== cartSubtotal) || (autoPromoApplied && autoPromoDiscount > 0)
                            ? 'line-through text-gray-500'
                            : 'text-gray-800'
                            }`}>
                            {selectedRegion === 'indian' ? '₹' : '$'}{cartSubtotal}
                        </span>
                    </div>

                    {/* Manual Coupon Discount */}
                    {couponCodeVerify && finalAmount !== cartSubtotal && (
                        <div className="flex justify-between items-center bg-green-50 px-3 py-2 rounded-lg border border-green-200">
                            <div className="flex items-center space-x-2">
                                <Tag className="w-4 h-4 text-green-600" />
                                <div>
                                    <span className="text-sm text-green-700 font-medium">Coupon Applied</span>
                                    <div className="text-xs text-green-600">Code: {couponCode}</div>
                                    {/* Show per-participant breakdown for fixed coupons */}
                                    {couponData.type === 'fixed' && couponData.selectedCategoryTickets && couponData.selectedCategoryTickets.length > 0 && (
                                        <div className="text-xs text-green-600 mt-1">
                                            {(() => {
                                                let totalParticipants = 0;
                                                let affectedCategories = [];
                                                cart.forEach(item => {
                                                    if (couponData.selectedCategoryTickets.includes(item.category)) {
                                                        totalParticipants += item.quantity;
                                                        affectedCategories.push(`${item.category} (${item.quantity})`);
                                                    }
                                                });
                                                return (
                                                    <div>
                                                        <div>Applied to: {affectedCategories.join(', ')}</div>
                                                        <div>{selectedRegion === 'indian' ? '₹' : '$'}{couponData.value} × {totalParticipants} participants</div>
                                                    </div>
                                                );
                                            })()}
                                        </div>
                                    )}
                                    {/* Show percentage breakdown */}
                                    {/* {couponData.type === 'percentage' && (
                                        <div className="text-xs text-green-600 mt-1">
                                            {couponData.value}% discount on total
                                        </div>
                                    )} */}
                                </div>
                            </div>
                            <span className="text-sm font-semibold text-green-700">
                                -{selectedRegion === 'indian' ? '₹' : '$'}
                                {(() => {
                                    if (couponData.type === 'percentage') {
                                        return Math.round(cartSubtotal * (couponData.value / 100));
                                    } else if (couponData.type === 'fixed') {
                                        // Check if it's category-specific
                                        if (couponData.selectedCategoryTickets && couponData.selectedCategoryTickets.length > 0) {
                                            let totalParticipants = 0;
                                            cart.forEach(item => {
                                                if (couponData.selectedCategoryTickets.includes(item.category)) {
                                                    totalParticipants += item.quantity;
                                                }
                                            });
                                            return couponData.value * totalParticipants;
                                        } else {
                                            return couponData.value;
                                        }
                                    }
                                    return cartSubtotal - finalAmount;
                                })()}
                            </span>
                        </div>
                    )}

                    {/* Auto Promo Discount */}
                    {!couponCodeVerify && autoPromoApplied && autoPromoDiscount > 0 && (
                        <div className="flex justify-between items-center bg-purple-50 px-3 py-2 rounded-lg border border-purple-200">
                            <div className="flex items-center space-x-2">
                                <Zap className="w-4 h-4 text-purple-600" />
                                <div>
                                    <span className="text-sm text-purple-700 font-medium">Auto Bulk Discount</span>
                                    <div className="text-xs text-purple-600">
                                        {eventData?.autoPromoSettings?.discountPercentage}% off for {eventData?.autoPromoSettings?.minQuantity}+ tickets
                                    </div>
                                    {/* Show which categories qualified */}
                                    <div className="text-xs text-purple-600 mt-1">
                                        Applied to: {autoPromoCategories.map(cartItemId => {
                                            const item = cart.find(c => c.id === cartItemId);
                                            return item ? `${item.category} (${item.quantity})` : '';
                                        }).filter(Boolean).join(', ')}
                                    </div>
                                </div>
                            </div>
                            <span className="text-sm font-semibold text-purple-700">
                                -{selectedRegion === 'indian' ? '₹' : '$'}{autoPromoDiscount}
                            </span>
                        </div>
                    )}

                    {/* Savings Summary */}
                    {((couponCodeVerify && finalAmount !== cartSubtotal) || (autoPromoApplied && autoPromoDiscount > 0)) && (
                        <div className="bg-gradient-to-r from-green-50 to-emerald-50 px-3 py-2 rounded-lg border border-green-200">
                            <div className="flex justify-between items-center">
                                <div className="flex items-center space-x-2">
                                    <Sparkles className="w-4 h-4 text-green-600" />
                                    <span className="text-sm font-medium text-green-700">Total Savings</span>
                                </div>
                                <span className="text-sm font-bold text-green-700">
                                    {selectedRegion === 'indian' ? '₹' : '$'}{cartSubtotal - finalAmount}
                                </span>
                            </div>
                        </div>
                    )}

                    {/* Final Total */}
                    <div className="border-t border-gray-300 pt-3 mt-4">
                        <div className="flex justify-between items-center">
                            <span className="font-bold text-lg md:text-xl text-gray-800">Final Total</span>
                            <span className="font-bold text-xl md:text-2xl text-gray-800">
                                {selectedRegion === 'indian' ? '₹' : '$'}{finalAmount}
                            </span>
                        </div>

                        {/* Payment method indicator */}
                        <div className="flex items-center justify-end space-x-2 mt-2">
                            <CreditCard className="w-4 h-4 text-gray-500" />
                            <span className="text-xs text-gray-500">
                                Payment via Razorpay (Card/UPI/Net Banking)
                            </span>
                        </div>
                    </div>

                    {/* Participant Count Summary */}
                    <div className="bg-blue-50 px-3 py-2 rounded-lg border border-blue-200 mt-4">
                        <div className="flex justify-between items-center">
                            <div className="flex items-center space-x-2">
                                <User className="w-4 h-4 text-blue-600" />
                                <span className="text-sm font-medium text-blue-700">Total Participants</span>
                            </div>
                            <span className="text-sm font-bold text-blue-700">{totalTickets}</span>
                        </div>

                        {/* Breakdown by category */}
                        <div className="mt-2 space-y-1">
                            {cart.map(item => (
                                <div key={item.id} className="flex justify-between text-xs text-blue-600">
                                    <span>{item.category} ({item.region})</span>
                                    <span>{item.quantity} participant{item.quantity > 1 ? 's' : ''}</span>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Terms & Important Notes */}
                    <div className="bg-amber-50 px-3 py-2 rounded-lg border border-amber-200 mt-4">
                        <div className="flex items-start space-x-2">
                            <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                            <div className="text-xs text-amber-700">
                                <div className="font-medium mb-1">Important Notes:</div>
                                <ul className="space-y-1">
                                    <li>• Accommodation is not included in ticket prices</li>
                                    <li>• All prices are inclusive of applicable taxes</li>
                                    <li>• Tickets are non-refundable once purchased</li>
                                    <li>• ID cards will be generated after successful payment</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

        </div>
    );
});



const RegistrationModalV2 = ({ toggleModal, isModal, fetchData }) => {
    const location = useLocation();
    const navigate = useNavigate();

    // Basic states
    const [eventData, setEventData] = useState(null);
    const [eventId, setEventId] = useState("");
    const [eventName, setEventName] = useState("");
    const [isCreating, setIsCreating] = useState(false);
    const [progressCount, setProgressCount] = useState(0);
    // Region selection
    const [selectedRegion, setSelectedRegion] = useState("");

    // Cart & steps
    const [cart, setCart] = useState([]);
    const [currentStep, setCurrentStep] = useState(1);
    const [participantDetails, setParticipantDetails] = useState([]);

    // Mobile-specific states
    const [isCartExpanded, setIsCartExpanded] = useState(false);

    // Coupon
    const [couponCode, setCouponCode] = useState("");
    const [couponCodeVerify, setCouponCodeVerify] = useState("");
    const [couponData, setCouponData] = useState({ type: null, value: 0 });
    const [isChecking, setIsChecking] = useState(false);
    const [utmSource, setUtmSource] = useState("");
    const [utmMedium, setUtmMedium] = useState("");
    const [utmCampaign, setUtmCampaign] = useState("");
    const [utmTerm, setUtmTerm] = useState("");
    const [utmContent, setUtmContent] = useState("");
    const [utm_id, setUtm_id] = useState(null);
    // Auto promo states
    const [autoPromoApplied, setAutoPromoApplied] = useState(false);
    const [autoPromoCategories, setAutoPromoCategories] = useState([]);

    // Date selection states
    const [cartItemDates, setCartItemDates] = useState({});

    // UI
    const [showThanksPage, setShowThanksPage] = useState(false);
    const [showIdcardLink, setShowIdcardLink] = useState("");

    // Initialize event data from URL
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const id = params.get("eventid");
        const name = params.get("eventName");
        const utm_source = params.get("utm_source");
        const utm_medium = params.get("utm_medium");
        const utm_campaign = params.get("utm_campaign");
        const utm_term = params.get("utm_term");
        const utm_content = params.get("utm_content");
        const utm_id = params.get("utm_id");
        setEventId(id || "");
        setEventName(name || "");
        setUtmSource(utm_source || "");
        setUtmMedium(utm_medium || "");
        setUtmCampaign(utm_campaign || "");
        setUtmTerm(utm_term || "");
        setUtmContent(utm_content || "");
        setUtm_id(utm_id || null);
    }, [location]);

    const fetchEventData = useCallback(async () => {
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/api/events/${eventId}`
            );
            setEventData(response.data);

            // Set default region
            if (response.data?.regionPricings?.length > 0) {
                setSelectedRegion(response.data.regionPricings[0].region);
            }
        } catch (error) {
            console.error("Error fetching event data:", error);
        }
    }, [eventId]);

    useEffect(() => {
        if (eventId) {
            fetchEventData();
        }
    }, [eventId, fetchEventData]);

    // Available regions/categories
    const availableRegions = useMemo(
        () => eventData?.regionPricings?.map((rp) => rp.region) || [],
        [eventData]
    );

    const availableCategories = useMemo(() => {
        if (!selectedRegion || !eventData?.regionPricings) return [];
        const categories = eventData.regionPricings.find((rp) => rp.region === selectedRegion)?.categories || [];

        // Sort by order field, fallback to original order if no order field exists
        return categories.sort((a, b) => (a.order || 0) - (b.order || 0));
    }, [selectedRegion, eventData]);

    // Check for auto promo eligibility
    const checkAutoPromo = useCallback((currentCart) => {
        if (!eventData?.autoPromoSettings?.enabled || couponCodeVerify === true) {
            setAutoPromoApplied(false);
            setAutoPromoCategories([]);
            return false;
        }

        const { minQuantity, discountPercentage } = eventData.autoPromoSettings;
        const categoryQuantities = {};
        const eligibleCategories = [];

        // Count quantities per category
        currentCart.forEach(item => {
            const key = `${item.region}-${item.category}`;
            categoryQuantities[key] = (categoryQuantities[key] || 0) + item.quantity;
        });

        // Check which categories qualify for auto promo
        Object.entries(categoryQuantities).forEach(([key, quantity]) => {
            if (quantity >= minQuantity) {
                eligibleCategories.push(key);
            }
        });

        if (eligibleCategories.length > 0) {
            setAutoPromoApplied(true);
            setAutoPromoCategories(eligibleCategories);
            return true;
        } else {
            setAutoPromoApplied(false);
            setAutoPromoCategories([]);
            return false;
        }
    }, [eventData, couponCodeVerify]);

    // Cart helpers with auto promo check
    const addToCart = useCallback((region, category, quantity = 1, extraData) => {
        const categoryData = eventData?.regionPricings
            .find(r => r.region === region)
            ?.categories.find(c => c.name === category);

        if (!categoryData) return;

        // Check if coupon applies from regular price
        const useRegularPrice = couponCodeVerify && couponData?.applyFromRegularPrice;

        // Use regular price if applyFromRegularPrice is true, otherwise use early bird
        const effectivePrice = useRegularPrice
            ? categoryData.regularPrice
            : (categoryData.earlyBirdPrice || categoryData.regularPrice || categoryData.price || 0);

        const cartItemId = `${region}-${category}`;

        setCart(prevCart => {
            const existingItem = prevCart.find(item => item.id === cartItemId);
            let newCart;

            if (existingItem) {
                newCart = prevCart.map(item =>
                    item.id === cartItemId
                        ? {
                            ...item,
                            quantity: item.quantity + quantity,
                            totalPrice: effectivePrice * (item.quantity + quantity),
                        }
                        : item
                );
            } else {
                newCart = [
                    ...prevCart,
                    {
                        id: cartItemId,
                        region,
                        category,
                        categoryData,
                        quantity,
                        price: effectivePrice,
                        totalPrice: effectivePrice * quantity,
                    }
                ];
            }

            // Check auto promo after cart update
            setTimeout(() => checkAutoPromo(newCart), 0);
            return newCart;
        });

        // Reset manual coupon on cart change
        setCouponCode('');
        setCouponCodeVerify(null);
    }, [eventData, checkAutoPromo, couponCodeVerify, couponData]);


    const updateCartQuantity = useCallback((cartItemId, newQuantity) => {
        if (newQuantity <= 0) {
            setCart(prev => {
                const newCart = prev.filter(i => i.id !== cartItemId);
                setTimeout(() => checkAutoPromo(newCart), 0);
                return newCart;
            });
            // Reset coupon when cart becomes empty
            setCouponCode('');
            setCouponCodeVerify(null);
            setAutoPromoCategories([]);
            return;
        }

        setCart(prevCart => {
            const newCart = prevCart.map(item =>
                item.id === cartItemId
                    ? { ...item, quantity: newQuantity, totalPrice: item.price * newQuantity }
                    : item
            );
            setTimeout(() => checkAutoPromo(newCart), 0);
            return newCart;
        });

        // Reset coupon when cart contents change
        setCouponCode('');
        setCouponCodeVerify(null);
    }, [checkAutoPromo]);

    const removeFromCart = useCallback((cartItemId) => {
        setCart((prevCart) => {
            const newCart = prevCart.filter((item) => item.id !== cartItemId);
            setTimeout(() => checkAutoPromo(newCart), 0);
            return newCart;
        });
        setCouponCode("");
        setCouponCodeVerify("");
    }, [checkAutoPromo]);

    const clearCart = useCallback(() => {
        setCart([]);
        setParticipantDetails([]);
        setCurrentStep(1);
        setCouponCode("");
        setCouponCodeVerify("");
        setAutoPromoApplied(false);
        setAutoPromoCategories([]);
        setCartItemDates({});
        setIsCartExpanded(false);
    }, []);

    // Totals with auto promo calculation
    const cartSubtotal = useMemo(() => {
        // If applyFromRegularPrice is true, recalculate cart with regular prices
        if (couponCodeVerify && couponData?.applyFromRegularPrice) {
            return cart.reduce((sum, item) => {
                const categoryData = eventData?.regionPricings
                    .find(r => r.region === item.region)
                    ?.categories.find(c => c.name === item.category);

                const regularPrice = categoryData?.regularPrice || item.price;
                return sum + (regularPrice * item.quantity);
            }, 0);
        }

        // Normal calculation with early bird prices
        return cart.reduce((sum, item) => sum + item.totalPrice, 0);
    }, [cart, couponCodeVerify, couponData, eventData]);


    const autoPromoDiscount = useMemo(() => {
        if (!autoPromoApplied || !eventData?.autoPromoSettings?.enabled) return 0;

        const { discountPercentage } = eventData.autoPromoSettings;
        let totalDiscount = 0;

        autoPromoCategories.forEach(categoryKey => {
            const [region, category] = categoryKey.split('-');
            const cartItem = cart.find(item =>
                item.region === region && item.category === category
            );
            if (cartItem) {
                totalDiscount += (cartItem.totalPrice * discountPercentage) / 100;
            }
        });

        return Math.round(totalDiscount);
    }, [autoPromoApplied, autoPromoCategories, cart, eventData]);

    const totalTickets = useMemo(
        () => cart.reduce((sum, item) => sum + item.quantity, 0),
        [cart]
    );

    // Coupon styles (memoized)
    const couponInputStyles = useMemo(() => {
        if (couponCodeVerify === true) return "border-green-500 bg-green-50 focus:ring-green-200";
        if (couponCodeVerify === false) return "border-red-500 bg-red-50 focus:ring-red-200";
        return "border-gray-300 bg-white focus:ring-blue-200";
    }, [couponCodeVerify]);

    const couponButtonStyles = useMemo(() => {
        if (couponCodeVerify === true) return "bg-green-500 hover:bg-green-600";
        if (couponCodeVerify === false) return "bg-red-500 hover:bg-red-600";
        return "bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-400 hover:to-blue-500";
    }, [couponCodeVerify]);

    // Coupon functions
    const handleCouponCodeChange = useCallback(e => {
        setCouponCodeVerify(null);
        setCouponCode(e.target.value.toUpperCase());

        // If clearing coupon, reset cart prices back to early bird
        if (e.target.value === '') {
            // Reset couponData
            setCouponData({
                type: null,
                value: 0,
                selectedCategoryTickets: [],
                limit: null,
                usedCount: 0,
                remainingUses: null,
                applyFromRegularPrice: false,
            });

            // **CRITICAL FIX: Reset cart prices back to early bird**
            setCart(prevCart => prevCart.map(item => {
                const categoryData = eventData?.regionPricings
                    .find(r => r.region === item.region)
                    ?.categories.find(c => c.name === item.category);

                // Use early bird price (original logic)
                const earlyBirdPrice = categoryData?.earlyBirdPrice || categoryData?.regularPrice || categoryData?.price || 0;

                console.log(`Resetting: ${item.category}, From: ${item.price}, To Early Bird: ${earlyBirdPrice}`);

                return {
                    ...item,
                    price: earlyBirdPrice,
                    totalPrice: earlyBirdPrice * item.quantity
                };
            }));

            // Re-check auto promo
            setTimeout(() => checkAutoPromo(cart), 0);
        }
    }, [cart, checkAutoPromo, eventData]);


    const checkCouponCode = useCallback(async (e) => {
        e.preventDefault();
        if (!cart.length || !selectedRegion) return;

        try {
            setIsChecking(true);
            const cartQueryParam = encodeURIComponent(JSON.stringify(cart));
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/api/participants/check/couponcode/${couponCode}/event/${eventId}/scope/${selectedRegion}/couponcodecheck?cart=${cartQueryParam}&totalTickets=${totalTickets}`
            );
            // **ADD THIS DEBUG LOG FIRST**
            console.log('Full API Response:', response.data);
            console.log('Coupon Data:', {
                type: response.data.type,
                value: response.data,
                applyFromRegularPrice: response.data.applyFromRegularPrice,
                selectedCategoryTickets: response.data.selectedCategoryTickets
            });

            setIsChecking(false);
            setCouponCodeVerify(response.data.check);
            setCouponData({
                type: response.data.type,
                value: response.data.value,
                selectedCategoryTickets: response.data.selectedCategoryTickets || [],
                limit: response.data?.limit,
                usedCount: response.data?.usedCount,
                remainingUses: response.data?.remainingUses,
                applyFromRegularPrice: response.data?.applyFromRegularPrice || false,
            });

            if (response.data?.remainingUses !== null) {
                toast.dismiss();
                toast.success(`Coupon applied!`, { duration: 4000 });
            } else {
                toast.success('Coupon code applied successfully!');
            }

            // Disable auto promo when manual coupon is applied
            if (response.data.check) {
                setAutoPromoApplied(false);
                setAutoPromoCategories([]);

                // **CRITICAL FIX: Refresh cart prices if applyFromRegularPrice is true**
                if (response.data.applyFromRegularPrice) {
                    setCart(prevCart => prevCart.map(item => {
                        const categoryData = eventData?.regionPricings
                            .find(r => r.region === item.region)
                            ?.categories.find(c => c.name === item.category);

                        const regularPrice = categoryData?.regularPrice || item.price;

                        return {
                            ...item,
                            price: regularPrice,
                            totalPrice: regularPrice * item.quantity
                        };
                    }));
                }
            }
        } catch (error) {
            setIsChecking(false);
            setCouponCodeVerify(false);

            if (error.response?.data?.message) {
                toast.dismiss();
                toast.error(error.response.data.message);
            } else {
                toast.dismiss();

                toast.error('Invalid coupon code');
            }
            console.error('Error checking coupon code', error);
        }
    }, [cart.length, selectedRegion, couponCode, eventId, totalTickets, eventData]);


    const finalAmount = useMemo(() => {
        let total = cartSubtotal;

        // Apply manual coupon discount first
        if (couponCodeVerify && couponData.type) {
            if (couponData.type === 'percentage') {
                total = Math.round(total * (1 - couponData.value / 100));
            } else if (couponData.type === 'fixed') {
                // Check if coupon has selectedCategoryTickets
                if (couponData.selectedCategoryTickets && couponData.selectedCategoryTickets.length > 0) {
                    // Apply fixed discount per participant for specific categories only
                    let totalParticipants = 0;
                    cart.forEach(item => {
                        if (couponData.selectedCategoryTickets.includes(item.category)) {
                            totalParticipants += item.quantity;
                        }
                    });
                    const totalDiscount = couponData.value * totalParticipants;
                    total = Math.max(0, total - totalDiscount);
                } else {
                    // Apply fixed discount once for all categories (existing behavior)
                    total = Math.max(0, total - couponData.value);
                }
            }
        }
        // Apply auto promo discount if no manual coupon
        else if (autoPromoApplied) {
            total = Math.max(0, total - autoPromoDiscount);
        }

        return total;
    }, [couponCodeVerify, couponData, cartSubtotal, autoPromoApplied, autoPromoDiscount, cart]);



    // Step 2: initialize participant details
    const proceedToParticipantDetails = useCallback(() => {
        if (cart.length === 0) {
            toast.error("Please add tickets to cart first");
            return;
        }

        // Initialize participant details array with stable row ids
        const details = [];
        cart.forEach((cartItem, idx) => {
            for (let i = 0; i < cartItem.quantity; i++) {
                details.push({
                    _rowId: `${cartItem.id}-${i}`, // stable key
                    cartItemId: cartItem.id,
                    region: cartItem.region,
                    category: cartItem.category,
                    firstName: "",
                    email: "",
                    phone: "",
                    designation: "",
                    institute: "",
                    errors: {},
                });
            }
        });

        setParticipantDetails(details);
        setCurrentStep(2);
        setIsCartExpanded(false); // Close cart on mobile when proceeding
    }, [cart]);

    // Update participant detail (stable)
    const updateParticipantDetail = useCallback((index, field, value) => {
        setParticipantDetails((prev) =>
            prev.map((detail, i) => (i === index ? { ...detail, [field]: value } : detail))
        );
    }, []);

    // Validation
    // Update the validation function in your main component
    const validateParticipantDetails = useCallback(() => {
        let isValid = true;
        const updatedDetails = participantDetails.map((detail) => {
            const errors = {};
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            const phoneRegex = /^\d{10}$/;

            if (!detail.firstName?.trim()) errors.firstName = "First name is required";
            if (!detail.lastName?.trim()) errors.lastName = "Last name is required";
            if (!detail.email?.trim()) errors.email = "Email is required";
            if (detail.email.trim() && !emailRegex.test(detail.email.trim()))
                errors.email = "Invalid email";
            if (!detail.phone.trim()) errors.phone = "Phone is required";
            // else if (!phoneRegex.test(detail.phone.trim()))
            //     errors.phone = "Phone must be 10 digits";

            // Add validation for location fields
            // if (!detail.state?.trim()) errors.state = "State is required";
            if (!detail.city?.trim()) errors.city = "City is required";

            if (detail.region === "international") {
                if (!detail.country?.trim()) errors.country = "Country is required";
            }

            if (Object.keys(errors).length > 0) isValid = false;
            return { ...detail, errors };
        });
        console.log(updatedDetails);
        setParticipantDetails(updatedDetails);
        return isValid;
    }, [participantDetails]);

    // Add success handler
    const handlePaymentSuccess = useCallback(async (verificationData) => {
        try {
            // Store participant IDs in localStorage
            const storedParticipants = JSON.parse(localStorage.getItem('userCreatedParticipants') || '[]');
            const newParticipantIds = verificationData.participants.map(p => ({
                participantId: p._id,
                eventId: eventId,
                eventName: eventName,
                participantName: p.firstName,
                createdAt: new Date().toISOString(),
                link: `https://idcard.insideoutprojects.in/id-card/${eventId}/${p._id}`
            }));

            const updatedParticipants = [...storedParticipants, ...newParticipantIds];
            localStorage.setItem('userCreatedParticipants', JSON.stringify(updatedParticipants));

            if (isModal) {
                toggleModal();
                fetchData?.();
            } else {
                setShowThanksPage(true);
                setShowIdcardLink(newParticipantIds[0]?.link);
            }

            // Clear cart and reset state
            setCart([]);
            setParticipantDetails([]);
            setCouponCode('');
            setCouponCodeVerify(null);
            setCurrentStep(1);

        } catch (error) {
            console.error('Error in payment success handler:', error);
        }
    }, [eventId, eventName, isModal, toggleModal, fetchData]);


    // Create participants with date information
    const createParticipants = useCallback(async () => {
        if (!validateParticipantDetails()) {
            toast.error("Please fix all validation errors");
            return;
        }



        setIsCreating(true);
        setProgressCount(0); // Reset progress counter

        try {
            // Generate a single order ID for all participants in this purchase
            const orderId = `ORDER-${Date.now()}-${Math.random().toString(36).substr(2, 6)}`;
            const createdParticipants = [];
            const totalParticipants = participantDetails.length;

            for (let index = 0; index < totalParticipants; index++) {
                const detail = participantDetails[index];
                const cartItem = cart.find(item => item.id === detail.cartItemId);
                if (!cartItem) continue;

                // Update progress count before processing each participant
                setProgressCount(index + 1);

                // Create full name from first and last name
                const fullName = `${(detail.firstName || '').trim()} ${(detail.lastName || '').trim()}`.trim();
                const formData = new FormData();

                formData.append("firstName", detail.firstName);
                formData.append("lastName", detail.lastName || "");
                formData.append("fullName", fullName);
                formData.append("designation", detail.designation || "");
                formData.append("institute", detail.institute || "");
                formData.append("eventId", eventId);
                formData.append("phone", detail.phone);
                formData.append("eventName", eventName);
                formData.append("email", detail.email);
                formData.append("quantity", 1);
                formData.append("ticketRegion", detail.region);
                formData.append("ticketCategory", detail.category);
                formData.append("ticketPrice", cartItem.price.toString());
                formData.append("discountedPrice", cartItem.price.toString());
                formData.append("currency", selectedRegion === "indian" ? "INR" : "USD");
                formData.append("orderId", orderId);
                formData.append("countryCode", detail.countryCode || (detail.region === "indian" ? "+91" : "+1"));
                formData.append("totalOrderAmount", finalAmount.toString());
                formData.append("paymentMethod", finalAmount > 0 ? "razorpay" : "free");
                formData.append("city", detail.city || "");
                formData.append("state", detail.state || "");
                formData.append("country", detail.country || "");

                // Add selected dates if any
                const itemDates = cartItemDates[cartItem.id];
                if (itemDates && itemDates.length > 0) {
                    formData.append("selectedDates", JSON.stringify(itemDates));
                }

                // Handle discount information
                if (couponCodeVerify && couponData.value > 0) {
                    formData.append("couponDiscount",
                        couponData.type === "percentage"
                            ? Math.round((cartSubtotal * couponData.value) / 100).toString()
                            : couponData.value.toString()
                    );
                    formData.append("useCouponCode", couponCode);
                } else if (autoPromoApplied && autoPromoDiscount > 0) {
                    formData.append("couponDiscount", autoPromoDiscount.toString());
                    formData.append("useCouponCode", `AUTO-BULK-${eventData.autoPromoSettings.discountPercentage}%`);
                }

                if (eventData?.idcardimage) formData.append("backgroundImage", eventData?.idcardimage);

                const response = await axios.post(
                    `${process.env.REACT_APP_API_URL}/api/participants/with-ticket-details`,
                    formData,
                    {
                        headers: { "Content-Type": "multipart/form-data" },
                    }
                );

                createdParticipants.push(response.data);

                // Add a small delay to allow React to update the progress state
                await new Promise(resolve => setTimeout(resolve, 0));
            }

            // WhatsApp notifications
            if (eventData?.whatsappApiKey) {
                for (const participant of createdParticipants) {
                    await handleSend(participant?.participant, eventData?.whatsappApiKey, eventName, participant?.link, participant?.idCard?.imageUrl);
                }
            }

            // Store participant IDs in localStorage
            const storedParticipants = JSON.parse(localStorage.getItem('userCreatedParticipants') || '[]');
            const newParticipantIds = createdParticipants.map(p => ({
                participantId: p.participant._id,
                eventId: eventId,
                eventName: eventName,
                participantName: p.participant.firstName,
                createdAt: new Date().toISOString(),
                link: p.link
            }));

            const updatedParticipants = [...storedParticipants, ...newParticipantIds];
            localStorage.setItem('userCreatedParticipants', JSON.stringify(updatedParticipants));

            const token = new URLSearchParams(window.location.search).get("token");
            if (token) {
                await axios.post(
                    `${process.env.REACT_APP_API_URL}/api/participants/invalidate-token`,
                    { token }
                );
            }

            if (isModal) {
                toggleModal();
                fetchData && fetchData();
            } else {
                setShowThanksPage(true);
                setShowIdcardLink(createdParticipants[0]?.link || "");
            }

            Swal.fire("Success", "All participants registered successfully!", "success");

            if (token) {
                navigate(`/id-created?eventid=${eventId}&eventName=${eventName}`);
            }
        } catch (error) {
            console.error("Error creating participants:", error);
            Swal.fire("Error", "Failed to create participants. Please try again.");
        } finally {
            setIsCreating(false);
            setProgressCount(0); // Reset progress counter
        }
    }, [
        validateParticipantDetails, participantDetails, cart, cartSubtotal, couponCode,
        couponCodeVerify, couponData, selectedRegion, finalAmount, eventData?.idcardimage,
        eventData?.whatsappApiKey, eventId, eventName, isModal, toggleModal, fetchData,
        navigate, autoPromoApplied, autoPromoDiscount, cartItemDates
    ]);

    // WhatsApp send (same as before)
    const handleSend = async (data, apiKey, eventName, link, generatedIdCardUrl) => {
        if (!apiKey) return;
        console.log("data", data);
        const payload = {
            apiKey,
            campaignName: "MYUTicketAPI",
            destination: data.countryCode + data.phone,
            userName: eventName,
            templateParams: [data.firstName, link],
            source: "new-landing-page form",
            media: {
                url: generatedIdCardUrl,
                filename: "MYU25-ticket.png"
            }
        };
        // console.log("payload", payload);

        try {
            await fetch("https://backend.aisensy.com/campaign/t1/api/v2", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
            });
        } catch (error) {
            console.error("Error sending WhatsApp message:", error);
        }
    };



    const ThanksPage = () => (
        <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 flex items-center justify-center p-4">
            <div className="bg-white/80 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/20 p-6 md:p-8 w-full max-w-lg text-center">
                <div className="mb-6 md:mb-8">
                    <div className="relative mx-auto w-16 h-16 md:w-20 md:h-20 mb-4 md:mb-6">
                        <div className="absolute inset-0 bg-gradient-to-r from-green-400 to-emerald-500 rounded-full animate-pulse"></div>
                        <div className="relative bg-white rounded-full p-3 md:p-4 shadow-lg">
                            <CheckCircle className="w-10 h-10 md:w-12 md:h-12 text-green-500" />
                        </div>
                    </div>
                    <h1 className="text-2xl md:text-4xl font-bold mb-3 md:mb-4 bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">
                        Registration Successful!
                    </h1>
                    <p className="text-gray-600 text-base md:text-lg leading-relaxed">
                        All participants for <span className="font-semibold text-indigo-600">{eventName}</span> have been registered successfully.
                    </p>
                </div>

                <div className="space-y-4 md:space-y-6">
                    <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl p-3 md:p-4">
                        <p className="text-green-700 text-xs md:text-sm font-medium">
                            You will receive confirmation whatsapp message and emails with event details for all participants.
                        </p>
                    </div>

                    <div className="space-y-3">
                        <button
                            onClick={() => {
                                setShowThanksPage(false);
                                clearCart();
                            }}
                            className="w-full py-3 md:py-4 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold rounded-xl hover:from-indigo-700 hover:to-purple-700 transform hover:scale-[1.02] transition-all duration-200 shadow-lg hover:shadow-xl text-sm md:text-base"
                        >
                            Register More Participants
                        </button>

                        <button
                            onClick={() => navigate('/my-id-cards')}
                            className="w-full py-3 md:py-4 bg-gradient-to-r from-blue-600 to-cyan-600 text-white font-semibold rounded-xl hover:from-blue-700 hover:to-cyan-700 transform hover:scale-[1.02] transition-all duration-200 shadow-lg hover:shadow-xl text-sm md:text-base"
                        >
                            View My ID Cards
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );

    // When switching regions, clear cart & coupon
    const handleRegionChange = useCallback(
        (region) => {
            setSelectedRegion(region);
            setCart([]);
            setParticipantDetails([]);
            setCouponCode("");
            setCouponCodeVerify("");
            setAutoPromoApplied(false);
            setAutoPromoCategories([]);
            setCartItemDates({});
            setIsCartExpanded(false);
        },
        []
    );

    if (showThanksPage) return <ThanksPage />;

    if (!eventData) {
        return (
            <div className="flex items-center justify-center p-8">
                <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
            </div>
        );

    }
    const validateDateRequirements = () => {
        const errors = [];

        cart.forEach(item => {
            const category = availableCategories.find(c => c.name === item.category);
            if (category?.enableDateSelection && category?.availableDates?.length > 0) {
                const selectedDates = getCategoryDates(item.region, item.category);
                const minRequired = category.minDateSelection || 1;

                if (selectedDates.length < minRequired) {
                    errors.push(`${item.category}: Select at least ${minRequired} date${minRequired > 1 ? 's' : ''}`);
                }
            }
        });

        return errors;
    };


    // Check if dates are selected for a category
    const getCategoryDates = (region, categoryName) => {
        const cartItemId = `${region}-${categoryName}`;
        return cartItemDates[cartItemId] || [];
    };


    // Enhanced proceed function with validation
    const handleProceedToParticipantDetails = () => {
        if (cart.length === 0) {
            toast.error("Please add tickets to cart first");
            return;
        }

        // Validate date requirements
        const dateErrors = validateDateRequirements();
        if (dateErrors.length > 0) {
            toast.error(`Please complete date selection:\n${dateErrors.join('\n')}`);
            return;
        }

        proceedToParticipantDetails();
    };




    return (
        <>
            {isCreating && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
                    <div className="bg-white rounded-xl p-6 md:p-8 shadow-2xl text-center max-w-md mx-4">
                        {/* Warning Icon */}
                        <div className="flex justify-center mb-4">
                            <div className="relative">
                                <Loader2 className="h-10 w-10 md:h-12 md:w-12 animate-spin text-blue-600" />
                                <div className="absolute -top-2 -right-2 bg-amber-500 rounded-full p-1">
                                    <AlertTriangle className="h-3 w-3 text-white" />
                                </div>
                            </div>
                        </div>

                        {/* Main Title */}
                        <h3 className="text-lg md:text-xl font-semibold text-gray-800 mb-2">
                            Creating Participants
                        </h3>

                        {/* Progress Count */}
                        <p className="text-gray-600 text-sm md:text-base mb-4">
                            {progressCount} of {participantDetails.length} participants created...
                        </p>

                        {/* Progress Bar */}
                        <div className="mt-4 mb-6 bg-gray-200 rounded-full h-3 overflow-hidden">
                            <div
                                className="bg-gradient-to-r from-blue-500 to-blue-600 h-3 rounded-full transition-all duration-500 ease-out"
                                style={{ width: `${(progressCount / participantDetails.length) * 100}%` }}
                            ></div>
                        </div>

                        {/* Warning Messages */}
                        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
                            <div className="flex items-start space-x-3">
                                <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                                <div className="text-left">
                                    <h4 className="text-sm font-semibold text-amber-800 mb-2">
                                        Important: Please Don't Close This Window
                                    </h4>
                                    <ul className="text-xs text-amber-700 space-y-1">
                                        <li>• Do not reload or refresh the page</li>
                                        <li>• Do not close the browser tab</li>
                                        <li>• Keep your internet connection stable</li>
                                        <li>• Process will resume if briefly interrupted</li>
                                    </ul>
                                </div>
                            </div>
                        </div>

                        {/* Additional Info */}
                        <div className="text-xs text-gray-500 flex items-center justify-center space-x-2">
                            <Clock className="h-3 w-3" />
                            <span>This may take a few minutes to complete</span>
                        </div>

                        {/* Estimated Time (Optional) */}
                        {participantDetails.length > 10 && (
                            <div className="mt-2 text-xs text-gray-400">
                                Estimated time: ~{Math.ceil(participantDetails.length * 2 / 60)} minutes
                            </div>
                        )}
                    </div>
                </div>
            )}


            <div className="relative w-full overflow-hidden pb-[50px]">
                <div className="relative bg-white rounded-none md:rounded-2xl shadow-none md:shadow-2xl border-0 md:border border-gray-100">
                    {/* Header */}
                    <div className="relative bg-[#E47378] px-4 py-4 md:px-8 md:py-6 rounded-none md:rounded-t-2xl">
                        <div className="relative z-10 flex items-center justify-between">
                            <div>
                                <h2 className="text-xl md:text-3xl font-bold text-white">
                                    {currentStep === 1 && "Choose Your Tickets"}
                                    {currentStep === 2 && "Participant Details"}
                                </h2>
                                <p className="text-indigo-100 mt-1 text-sm md:text-base">
                                    {currentStep === 1 && "Select region and add tickets to cart"}
                                    {currentStep === 2 && "Enter details for each participant"}
                                </p>
                            </div>
                            {isModal && (
                                <button
                                    type="button"
                                    onClick={toggleModal}
                                    className="group w-8 h-8 md:w-10 md:h-10 bg-white/20 hover:bg-white/30 rounded-xl flex items-center justify-center backdrop-blur-sm transition-all duration-300 hover:scale-105"
                                >
                                    <X className="w-4 h-4 md:w-5 md:h-5 text-white group-hover:rotate-90 transition-transform duration-300" />
                                </button>
                            )}
                        </div>

                        {/* Step Indicator */}
                        <div className="mt-3 md:mt-4 flex space-x-2">
                            <div className={`h-1 flex-1 rounded-full ${currentStep >= 1 ? "bg-white" : "bg-white/30"}`} />
                            <div className={`h-1 flex-1 rounded-full ${currentStep >= 2 ? "bg-white" : "bg-white/30"}`} />
                        </div>
                    </div>

                    {/* Content */}
                    <div className={`overflow-y-auto custom-scrollbar ${isModal ? "max-h-[70vh]" : ""}`}>
                        <div className=" md:p-6 bg-gradient-to-br from-slate-50 via-white to-blue-50/30">
                            {currentStep === 1 && (
                                <TicketSelectionStep
                                    availableRegions={availableRegions}
                                    selectedRegion={selectedRegion}
                                    setSelectedRegion={handleRegionChange}
                                    availableCategories={availableCategories}
                                    addToCart={addToCart}
                                    cart={cart}
                                    totalTickets={totalTickets}
                                    couponCode={couponCode}
                                    couponCodeVerify={couponCodeVerify}
                                    isChecking={isChecking}
                                    handleCouponCodeChange={handleCouponCodeChange}
                                    checkCouponCode={checkCouponCode}
                                    couponInputStyles={couponInputStyles}
                                    couponButtonStyles={couponButtonStyles}
                                    removeFromCart={removeFromCart}
                                    updateCartQuantity={updateCartQuantity}
                                    cartSubtotal={cartSubtotal}
                                    finalAmount={finalAmount}
                                    proceedToParticipantDetails={proceedToParticipantDetails}
                                    isCartExpanded={isCartExpanded}
                                    setIsCartExpanded={setIsCartExpanded}
                                    // Auto promo props
                                    autoPromoApplied={autoPromoApplied}
                                    autoPromoCategories={autoPromoCategories}
                                    autoPromoDiscount={autoPromoDiscount}
                                    eventData={eventData}
                                    // Date selection props
                                    cartItemDates={cartItemDates}
                                    setCartItemDates={setCartItemDates}
                                    couponData={couponData}
                                />
                            )}

                            {currentStep === 2 && (
                                <ParticipantDetailsStep
                                    participantDetails={participantDetails}
                                    updateParticipantDetail={updateParticipantDetail}
                                    cart={cart}
                                    selectedRegion={selectedRegion}
                                    finalAmount={finalAmount}
                                    setCurrentStep={setCurrentStep}
                                    cartItemDates={cartItemDates}
                                    cartSubtotal={cartSubtotal}
                                    totalTickets={totalTickets}
                                    couponCodeVerify={couponCodeVerify}
                                    couponCode={couponCode}
                                    couponData={couponData}
                                    autoPromoApplied={autoPromoApplied}
                                    autoPromoDiscount={autoPromoDiscount}
                                    autoPromoCategories={autoPromoCategories}
                                    eventData={eventData}
                                />
                            )}
                        </div>
                    </div>

                    {/* Desktop Footer */}
                    <div className="hidden lg:block bg-gray-50 px-6 py-4 border-t border-gray-200 rounded-b-2xl">
                        <div className="flex gap-3">
                            {/* Cancel/Back */}
                            {currentStep === 1 && isModal && (
                                <button
                                    type="button"
                                    onClick={toggleModal}
                                    className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 font-medium rounded-lg hover:bg-gray-200 transition-colors duration-200 flex items-center justify-center space-x-2"
                                >
                                    <X className="w-4 h-4" />
                                    <span>Cancel</span>
                                </button>
                            )}

                            {currentStep === 2 && (
                                <button
                                    onClick={() => setCurrentStep(1)}
                                    className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 font-medium rounded-lg hover:bg-gray-200 transition-colors duration-200 flex items-center justify-center space-x-2"
                                >
                                    <span>← Back to Tickets</span>
                                </button>
                            )}

                            {/* Next/Complete */}
                            {currentStep === 2 && (
                                <>
                                    {eventData?.isPaidEvent && finalAmount > 0 ? (
                                        <div className="flex-1">

                                            <RazorpayButton
                                                RazorPaySecret={eventData?.razorpaySecret}
                                                RazorpayApiKey={eventData?.razorpayKey}
                                                styleClass="w-full py-3 px-4 font-medium rounded-lg transition-all duration-200 flex items-center justify-center space-x-2 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white shadow-lg text-sm"
                                                onSuccess={handlePaymentSuccess} // New callback
                                                buttonText={`Pay ${selectedRegion === 'indian' ? '₹' : '$'}${finalAmount}`}
                                                amount={finalAmount}
                                                user={{
                                                    firstName: participantDetails[0]?.firstName,
                                                    email: participantDetails[0]?.email,
                                                    phone: participantDetails[0]?.phone,
                                                }}
                                                participantDetails={participantDetails.map(detail => ({
                                                    firstName: detail.firstName,
                                                    lastName: detail.lastName,
                                                    fullName: `${detail.firstName} ${detail.lastName}`.trim(),
                                                    email: detail.email,
                                                    phone: detail.phone,
                                                    countryCode: detail.countryCode || (detail.region === 'indian' ? '91' : '1'),
                                                    city: detail.city,
                                                    state: detail.state,
                                                    country: detail.country,
                                                    designation: detail.designation,
                                                    institute: detail.institute,
                                                    ticketRegion: detail.region,
                                                    ticketCategory: detail.category,
                                                    ticketPrice: cart.find(item => item.id === detail.cartItemId)?.price,
                                                    discountedPrice: cart.find(item => item.id === detail.cartItemId)?.price,
                                                    currency: selectedRegion === 'indian' ? 'INR' : 'USD',
                                                    selectedDates: cartItemDates[detail.cartItemId] || [],
                                                    utmSource: utmSource,
                                                    utmMedium: utmMedium,
                                                    utmCampaign: utmCampaign,
                                                    utmTerm: utmTerm,
                                                    utmContent: utmContent,
                                                    utm_id: utm_id
                                                }))}
                                                eventId={eventId}
                                                eventName={eventName}
                                                orderMetadata={{
                                                    cartItems: cart,
                                                    couponData: couponData,
                                                    autoPromoApplied: autoPromoApplied,
                                                    autoPromoDiscount: autoPromoDiscount,
                                                }}
                                                useCouponCode={couponCode}
                                                couponDiscount={couponCodeVerify ? (
                                                    couponData.type === 'percentage'
                                                        ? Math.round(cartSubtotal * (couponData.value / 100))
                                                        : couponData.value
                                                ) : 0}
                                                backgroundImage={eventData?.idcardimage}
                                                currency={selectedRegion === 'indian' ? 'INR' : 'USD'}
                                                loading={isCreating}
                                                onBeforePay={validateParticipantDetails}
                                            />

                                        </div>
                                    ) : (
                                        <button
                                            onClick={createParticipants}
                                            className="flex-1 bg-[#D66C81] hover:bg-[#C55A75] text-white font-medium py-3 px-4 rounded-lg transition-colors duration-200 flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                            disabled={isCreating}
                                        >
                                            {isCreating ? (
                                                <>
                                                    <Loader2 className="w-4 h-4 animate-spin" />
                                                    <span>Creating {progressCount}/{participantDetails.length}...</span>
                                                </>
                                            ) : (
                                                <>
                                                    <CheckCircle className="w-4 h-4" />
                                                    <span>Complete Registration</span>
                                                </>
                                            )}
                                        </button>
                                    )}
                                </>
                            )}
                        </div>
                    </div>

                    {/* Mobile: Sticky Bottom Bar */}
                    <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 z-40 shadow-lg">
                        {currentStep === 1 && (
                            <div className="flex items-center justify-between">
                                <div className="flex-1">
                                    <div className="text-xs text-gray-600 mb-1">
                                        {totalTickets} {totalTickets === 1 ? 'ticket' : 'tickets'} selected
                                    </div>
                                    <div className="font-semibold text-gray-800">
                                        Total: {selectedRegion === "indian" ? "₹" : "$"}{finalAmount}
                                        {autoPromoApplied && (
                                            <span className="text-xs text-green-600 ml-2">
                                                (Auto {eventData?.autoPromoSettings?.discountPercentage}% off applied!)
                                            </span>
                                        )}
                                    </div>
                                </div>
                                <button
                                    onClick={handleProceedToParticipantDetails}
                                    disabled={cart.length === 0}
                                    className="bg-[#D66C81] hover:bg-[#C55A75] disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-medium py-3 px-6 rounded-lg transition-colors duration-200 text-sm"
                                >
                                    Continue
                                </button>
                            </div>
                        )}

                        {currentStep === 2 && (
                            <div className="flex gap-3">
                                <button
                                    onClick={() => setCurrentStep(1)}
                                    className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 font-medium rounded-lg hover:bg-gray-200 transition-colors duration-200 text-sm"
                                >
                                    ← Back
                                </button>

                                {eventData?.isPaidEvent && finalAmount > 0 ? (
                                    <div className="flex-1">

                                        <RazorpayButton
                                            RazorPaySecret={eventData?.razorpaySecret}
                                            RazorpayApiKey={eventData?.razorpayKey}
                                            styleClass="w-full py-3 px-4 font-medium rounded-lg transition-all duration-200 flex items-center justify-center space-x-2 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white shadow-lg text-sm"
                                            onSuccess={handlePaymentSuccess} // New callback
                                            buttonText={`Pay ${selectedRegion === 'indian' ? '₹' : '$'}${finalAmount}`}
                                            amount={finalAmount}
                                            user={{
                                                firstName: participantDetails[0]?.firstName,
                                                email: participantDetails[0]?.email,
                                                phone: participantDetails[0]?.phone,
                                            }}
                                            participantDetails={participantDetails.map(detail => ({
                                                firstName: detail.firstName,
                                                lastName: detail.lastName,
                                                fullName: `${detail.firstName} ${detail.lastName}`.trim(),
                                                email: detail.email,
                                                phone: detail.phone,
                                                countryCode: detail.countryCode || (detail.region === 'indian' ? '91' : '1'),
                                                city: detail.city,
                                                state: detail.state,
                                                country: detail.country,
                                                designation: detail.designation,
                                                institute: detail.institute,
                                                ticketRegion: detail.region,
                                                ticketCategory: detail.category,
                                                ticketPrice: cart.find(item => item.id === detail.cartItemId)?.price,
                                                discountedPrice: cart.find(item => item.id === detail.cartItemId)?.price,
                                                currency: selectedRegion === 'indian' ? 'INR' : 'USD',
                                                selectedDates: cartItemDates[detail.cartItemId] || [],
                                                utmSource: utmSource,
                                                utmMedium: utmMedium,
                                                utmCampaign: utmCampaign,
                                                utmTerm: utmTerm,
                                                utmContent: utmContent,
                                                utm_id: utm_id
                                            }))}
                                            eventId={eventId}
                                            eventName={eventName}
                                            orderMetadata={{
                                                cartItems: cart,
                                                couponData: couponData,
                                                autoPromoApplied: autoPromoApplied,
                                                autoPromoDiscount: autoPromoDiscount,
                                            }}
                                            useCouponCode={couponCode}
                                            couponDiscount={couponCodeVerify ? (
                                                couponData.type === 'percentage'
                                                    ? Math.round(cartSubtotal * (couponData.value / 100))
                                                    : couponData.value
                                            ) : 0}
                                            backgroundImage={eventData?.idcardimage}
                                            currency={selectedRegion === 'indian' ? 'INR' : 'USD'}
                                            loading={isCreating}
                                            onBeforePay={validateParticipantDetails}
                                        />

                                    </div>
                                ) : (
                                    <button
                                        onClick={createParticipants}
                                        className="flex-1 bg-[#D66C81] hover:bg-[#C55A75] text-white font-medium py-3 px-4 rounded-lg transition-colors duration-200 flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                                        disabled={isCreating}
                                    >
                                        {isCreating ? (
                                            <>
                                                <Loader2 className="w-3 h-3 animate-spin" />
                                                <span>Creating {progressCount}/{participantDetails.length}...</span>
                                            </>
                                        ) : (
                                            <>
                                                <CheckCircle className="w-3 h-3" />
                                                <span>Complete</span>
                                            </>
                                        )}
                                    </button>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Custom Scrollbar Styles */}
                    <style>{`
                  .custom-scrollbar::-webkit-scrollbar { width: 6px; }
                  .custom-scrollbar::-webkit-scrollbar-track { background: rgba(229, 231, 235, 0.5); border-radius: 10px; }
                  .custom-scrollbar::-webkit-scrollbar-thumb { background: linear-gradient(to bottom, #3b82f6, #8b5cf6); border-radius: 10px; }
                  .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: linear-gradient(to bottom, #2563eb, #7c3aed); }
                `}</style>
                </div>
            </div>
        </>
    );
};

// Indian Flag Icon Component
const IndianFlagIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 480 480" className="w-full h-full">
        <circle cx="240" cy="240" r="240" fill="#f93" />
        <path fill="#fff" d="M0 160h480v160H0z" />
        <path fill="#128807" d="M0 320h480v160H0z" />
        <circle cx="240" cy="240" r="40" fill="#008" />
        <circle cx="240" cy="240" r="6" fill="#fff" />
    </svg>
);

export default RegistrationModalV2;
